/**
 * Practido Python/JS-to-Scratch Bi-Directional Synchronizer
 * Parses Python / JS text, groups statements into separate stacks/clusters,
 * updates existing block parameters, and dynamically creates new separate stacks.
 *
 * Key Capabilities:
 * 1. Bijective 1-to-1 conversion for say/think:
 *    - sprite.say("Hello!", 2) <-> looks_sayforsecs (MESSAGE, SECS)
 *    - sprite.say("Hello!")    <-> looks_say (MESSAGE)
 *    - sprite.think("Hmm...", 2) <-> looks_thinkforsecs (MESSAGE, SECS)
 *    - sprite.think("Hmm...")  <-> looks_think (MESSAGE)
 * 2. Backdrops & Costumes:
 *    - stage.switch_backdrop("backdrop1") <-> looks_switchbackdropto (with shadow menu)
 *    - stage.next_backdrop()             <-> looks_nextbackdrop
 *    - sprite.switch_costume("costume1") <-> looks_switchcostumeto (with shadow menu)
 *    - sprite.next_costume()             <-> looks_nextcostume
 *    - def on_backdrop_backdrop1():      <-> event_whenbackdropswitchesto
 * 3. Robust Pruning & Deletion Safety:
 *    - Deleting statements cleanly prunes blocks & shadow blocks without broken .next references
 *    - Deleting an entire function removes the stack cleanly
 *    - Deleting all code clears target.blocks without deleting the sprite/target asset
 * 4. Block set separation & Target isolation:
 *    - Blank lines or section headers at root indentation delineate separate stacks
 *    - Target isolation guarantees single-file or multi-file sprite independence
 */

import { sortTopLevelBlockKeys, toScratchMenuValue } from "./astEvaluator.js";

export const sanitizeHexColor = (colorStr, fallback = "#62b8c3") => {
  if (!colorStr) return fallback;
  let str = String(colorStr).replace(/["']/g, "").trim();
  if (!str.startsWith("#")) {
    str = "#" + str;
  }
  const hexPart = str.slice(1).replace(/[^0-9a-fA-F]/g, "");
  if (hexPart.length === 3) {
    return `#${hexPart[0]}${hexPart[0]}${hexPart[1]}${hexPart[1]}${hexPart[2]}${hexPart[2]}`.toLowerCase();
  }
  if (hexPart.length === 6) {
    return `#${hexPart}`.toLowerCase();
  }
  if (hexPart.length > 6) {
    return `#${hexPart.slice(0, 6)}`.toLowerCase();
  }
  if (hexPart.length > 0 && hexPart.length < 6) {
    return `#${hexPart.padEnd(6, "0")}`.toLowerCase();
  }
  return fallback;
};

const generateBlockId = () => "blk_" + Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 6);

// Recursively count blocks in a chain (including nested C-block substacks) for accurate visual stack height
const countBlocksInStack = (startKey, blocks) => {
  let count = 0;
  let curr = startKey;
  const visited = new Set();
  while (curr && blocks[curr] && !visited.has(curr)) {
    visited.add(curr);
    count += 1;
    const b = blocks[curr];
    if (b.inputs) {
      ["SUBSTACK", "SUBSTACK2"].forEach((subKey) => {
        const subInp = b.inputs[subKey];
        const subId = Array.isArray(subInp) ? subInp[1] : (subInp && (subInp.block || subInp.shadow));
        if (subId && typeof subId === "string" && blocks[subId]) {
          count += countBlocksInStack(subId, blocks);
        }
      });
    }
    curr = b.next;
  }
  return Math.max(1, count);
};

// Helper to safely delete a block and all its descendant blocks (shadows, inputs, reporter blocks, and nested substacks)
const deleteBlockTree = (blocks, blockId) => {
  const b = blocks[blockId];
  if (!b) return;
  if (b.inputs) {
    Object.values(b.inputs).forEach((inp) => {
      let childId = null;
      if (Array.isArray(inp) && inp.length >= 2) {
        if (typeof inp[1] === "string") childId = inp[1];
      } else if (inp && typeof inp === "object" && !Array.isArray(inp)) {
        childId = inp.block || inp.shadow;
      }
      if (childId && blocks[childId] && blocks[childId].parent === blockId) {
        let cur = childId;
        while (cur && blocks[cur]) {
          const nextCur = blocks[cur].next;
          deleteBlockTree(blocks, cur);
          cur = nextCur;
        }
      }
    });
  }
  delete blocks[blockId];
};

const deleteBlockAndShadows = deleteBlockTree;

// Helper to ensure a shadow menu block exists for costume/backdrop
const setShadowMenuInput = (blocks, parentBlock, inputName, menuOpcode, fieldName, fieldValue) => {
  let shadowId = null;
  if (parentBlock.inputs && parentBlock.inputs[inputName]) {
    const inp = parentBlock.inputs[inputName];
    if (Array.isArray(inp) && typeof inp[1] === "string") {
      shadowId = inp[1];
    } else if (typeof inp === "object" && !Array.isArray(inp)) {
      shadowId = inp.shadow || inp.block;
    }
  }

  if (!shadowId || !blocks[shadowId]) {
    shadowId = generateBlockId();
    blocks[shadowId] = {
      id: shadowId,
      opcode: menuOpcode,
      next: null,
      parent: parentBlock.id,
      inputs: {},
      fields: { [fieldName]: [String(fieldValue), null] },
      shadow: true,
      topLevel: false,
    };
    parentBlock.inputs[inputName] = [1, shadowId];
    return { oldVal: null, newVal: fieldValue };
  } else {
    const shadowBlock = blocks[shadowId];
    shadowBlock.opcode = menuOpcode;
    const f = shadowBlock.fields?.[fieldName];
    const oldVal = Array.isArray(f) ? f[0] : (typeof f === "object" && f ? f.value : f);
    if (Array.isArray(f)) {
      shadowBlock.fields[fieldName] = [String(fieldValue), null];
    } else if (typeof f === "object" && f) {
      shadowBlock.fields[fieldName] = { name: fieldName, value: String(fieldValue) };
    } else {
      shadowBlock.fields = { [fieldName]: [String(fieldValue), null] };
    }
    shadowBlock.parent = parentBlock.id;
    return oldVal !== fieldValue ? { oldVal, newVal: fieldValue } : null;
  }
};

const getListIdOrCreate = (projectObj, target, listName) => {
  if (!target.lists) target.lists = {};
  for (const [id, listData] of Object.entries(target.lists)) {
    if (Array.isArray(listData) && listData[0] && listData[0].toLowerCase() === listName.toLowerCase()) {
      return id;
    }
  }
  const stage = projectObj?.targets?.find((t) => t.isStage);
  if (stage && stage.lists) {
    for (const [id, listData] of Object.entries(stage.lists)) {
      if (Array.isArray(listData) && listData[0] && listData[0].toLowerCase() === listName.toLowerCase()) {
        return id;
      }
    }
  }
  const newListId = "list_" + Math.random().toString(36).substring(2, 10);
  const owner = stage || target;
  if (!owner.lists) owner.lists = {};
  owner.lists[newListId] = [listName, []];
  return newListId;
};

const getVariableIdOrCreate = (projectObj, target, varName) => {
  if (!target.variables) target.variables = {};
  for (const [id, varData] of Object.entries(target.variables)) {
    if (Array.isArray(varData) && varData[0] && varData[0].toLowerCase() === varName.toLowerCase()) {
      return id;
    }
  }
  const stage = projectObj?.targets?.find((t) => t.isStage);
  if (stage && stage.variables) {
    for (const [id, varData] of Object.entries(stage.variables)) {
      if (Array.isArray(varData) && varData[0] && varData[0].toLowerCase() === varName.toLowerCase()) {
        return id;
      }
    }
  }
  const newVarId = "var_" + Math.random().toString(36).substring(2, 10);
  const owner = stage || target;
  if (!owner.variables) owner.variables = {};
  owner.variables[newVarId] = [varName, 0];
  return newVarId;
};

const getBroadcastIdOrCreate = (projectObj, msgName) => {
  const stage = projectObj?.targets?.find((t) => t.isStage);
  if (!stage) return msgName;
  if (!stage.broadcasts) stage.broadcasts = {};
  for (const [id, name] of Object.entries(stage.broadcasts)) {
    if (name.toLowerCase() === msgName.toLowerCase()) {
      return id;
    }
  }
  const newId = "broadcast_" + Math.random().toString(36).substring(2, 10);
  stage.broadcasts[newId] = msgName;
  return newId;
};

const getLineIndent = (line) => {
  const m = line.match(/^([ \t]*)/);
  if (!m) return 0;
  return m[1].replace(/\t/g, "    ").length;
};

const splitCallArgs = (rawArgs) => {
  const args = [];
  let current = "";
  let inQuote = null;
  let escape = false;
  let parenDepth = 0;
  let bracketDepth = 0;
  let braceDepth = 0;

  for (let i = 0; i < rawArgs.length; i++) {
    const ch = rawArgs[i];
    if (escape) { current += ch; escape = false; continue; }
    if (ch === "\\") { current += ch; escape = true; continue; }
    if (inQuote) {
      current += ch;
      if (ch === inQuote) inQuote = null;
      continue;
    }
    if (ch === '"' || ch === "'") { inQuote = ch; current += ch; continue; }
    if (ch === "(") parenDepth++;
    else if (ch === ")") parenDepth--;
    else if (ch === "[") bracketDepth++;
    else if (ch === "]") bracketDepth--;
    else if (ch === "{") braceDepth++;
    else if (ch === "}") braceDepth--;

    if (ch === "," && parenDepth === 0 && bracketDepth === 0 && braceDepth === 0) {
      args.push(current.trim());
      current = "";
      continue;
    }
    current += ch;
  }
  if (current.trim() || args.length > 0) args.push(current.trim());
  return args;
};

const stripOuterParens = (str) => {
  let s = (str || "").trim();
  while (s.startsWith("(") && s.endsWith(")")) {
    let depth = 0;
    let canStrip = false;
    for (let i = 0; i < s.length; i++) {
      if (s[i] === "(") depth++;
      else if (s[i] === ")") depth--;
      if (depth === 0) {
        if (i === s.length - 1) canStrip = true;
        break;
      }
    }
    if (canStrip) {
      s = s.slice(1, -1).trim();
    } else {
      break;
    }
  }
  return s;
};

// Find the last occurrence of any operator in opList at depth 0 (left-associative search)
const findBinaryOp = (str, opList) => {
  let inQuote = null;
  let parenDepth = 0;
  let bracketDepth = 0;
  let braceDepth = 0;

  for (let i = str.length - 1; i >= 0; i--) {
    const ch = str[i];
    if (inQuote) {
      if (ch === inQuote && str[i - 1] !== "\\") inQuote = null;
      continue;
    }
    if (ch === '"' || ch === "'") { inQuote = ch; continue; }
    if (ch === ")") { parenDepth++; continue; }
    if (ch === "(") { parenDepth--; continue; }
    if (ch === "]") { bracketDepth++; continue; }
    if (ch === "[") { bracketDepth--; continue; }
    if (ch === "}") { braceDepth++; continue; }
    if (ch === "{") { braceDepth--; continue; }

    if (parenDepth === 0 && bracketDepth === 0 && braceDepth === 0) {
      for (const op of opList) {
        if (op === "and" || op === "or" || op === "in") {
          const start = i - op.length + 1;
          if (start >= 0 && str.substring(start, i + 1) === op) {
            const before = str[start - 1];
            const after = str[i + 1];
            const beforeOk = !before || /[\s()]/.test(before);
            const afterOk = !after || /[\s()]/.test(after);
            if (beforeOk && afterOk) {
              return {
                op,
                left: str.substring(0, start).trim(),
                right: str.substring(i + 1).trim()
              };
            }
          }
        } else {
          const start = i - op.length + 1;
          if (start >= 0 && str.substring(start, i + 1) === op) {
            if (op === "-") {
              const prev = str.substring(0, start).trim();
              if (!prev || /[+\-*/%=<>(,]/.test(prev.slice(-1))) {
                continue;
              }
            }
            return {
              op,
              left: str.substring(0, start).trim(),
              right: str.substring(i + 1).trim()
            };
          }
        }
      }
    }
  }
  return null;
};

const decomposeExpression = (rawExpr) => {
  if (rawExpr === null || rawExpr === undefined) return { type: "primitive", val: "" };
  let str = stripOuterParens(String(rawExpr).trim());
  if (!str) return { type: "primitive", val: "" };

  // 1. Quoted string literals
  if (/^"(?:[^"\\]|\\.)*"$/.test(str) || /^'(?:[^'\\]|\\.)*'$/.test(str)) {
    let clean = str.slice(1, -1);
    try { clean = JSON.parse(`"${clean.replace(/"/g, '\\"')}"`); } catch (e) {}
    return { type: "primitive", val: clean };
  }

  // 2. Pure numbers
  if (/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(str)) {
    return { type: "primitive", val: str };
  }

  // 3. Booleans
  if (str === "True" || str === "true") return { type: "primitive", val: "true" };
  if (str === "False" || str === "false") return { type: "primitive", val: "false" };

  // 4. Logical OR
  const orMatch = findBinaryOp(str, ["or", "||"]);
  if (orMatch) {
    return {
      type: "operator_or",
      left: decomposeExpression(orMatch.left),
      right: decomposeExpression(orMatch.right)
    };
  }

  // 5. Logical AND
  const andMatch = findBinaryOp(str, ["and", "&&"]);
  if (andMatch) {
    return {
      type: "operator_and",
      left: decomposeExpression(andMatch.left),
      right: decomposeExpression(andMatch.right)
    };
  }

  // 6. Logical NOT
  if (/^(?:not\s+|!\s*)/.test(str)) {
    const inner = str.replace(/^(?:not\s+|!\s*)/, "").trim();
    return {
      type: "operator_not",
      operand: decomposeExpression(inner)
    };
  }

  // 7. Comparison operators
  const compMatch = findBinaryOp(str, ["===", "==", "!==", "!=", "<=", ">=", "<", ">"]);
  if (compMatch) {
    const { op, left, right } = compMatch;
    if (op === ">" || op === ">=") {
      return {
        type: "operator_gt",
        left: decomposeExpression(left),
        right: decomposeExpression(right)
      };
    }
    if (op === "<" || op === "<=") {
      return {
        type: "operator_lt",
        left: decomposeExpression(left),
        right: decomposeExpression(right)
      };
    }
    if (op === "==" || op === "===") {
      return {
        type: "operator_equals",
        left: decomposeExpression(left),
        right: decomposeExpression(right)
      };
    }
    if (op === "!=" || op === "!==") {
      return {
        type: "operator_not",
        operand: {
          type: "operator_equals",
          left: decomposeExpression(left),
          right: decomposeExpression(right)
        }
      };
    }
  }

  // 7b. Membership / Contains operator: (item in list) or ("a" in "apple")
  const inMatch = findBinaryOp(str, ["in"]);
  if (inMatch) {
    const item = inMatch.left;
    const container = inMatch.right;
    return {
      type: "operator_contains",
      left: decomposeExpression(container),
      right: decomposeExpression(item)
    };
  }

  const includesMatch = str.match(/^(.*?)\.includes\s*\((.*?)\)$/);
  if (includesMatch) {
    return {
      type: "operator_contains",
      left: decomposeExpression(includesMatch[1]),
      right: decomposeExpression(includesMatch[2])
    };
  }

  // 8. Addition and Subtraction
  const addSubMatch = findBinaryOp(str, ["+", "-"]);
  if (addSubMatch) {
    const { op, left, right } = addSubMatch;
    const lDec = decomposeExpression(left);
    const rDec = decomposeExpression(right);
    if (op === "+" && (
      (lDec.type === "primitive" && typeof lDec.val === "string" && isNaN(Number(lDec.val))) ||
      (rDec.type === "primitive" && typeof rDec.val === "string" && isNaN(Number(rDec.val))) ||
      lDec.type === "operator_join" || rDec.type === "operator_join"
    )) {
      return { type: "operator_join", left: lDec, right: rDec };
    }
    return {
      type: op === "+" ? "operator_add" : "operator_subtract",
      left: lDec,
      right: rDec
    };
  }

  // 9. Multiplication, Division, Modulo
  const mulDivMatch = findBinaryOp(str, ["*", "/", "%"]);
  if (mulDivMatch) {
    const { op, left, right } = mulDivMatch;
    const lDec = decomposeExpression(left);
    const rDec = decomposeExpression(right);
    let type = "operator_multiply";
    if (op === "/") type = "operator_divide";
    else if (op === "%") type = "operator_mod";
    return { type, left: lDec, right: rDec };
  }

  // 10. Random: random.randint(from, to) or Math.floor(Math.random() * (to - from + 1)) + from
  const randMatch = str.match(/^(?:random\.randint|randint)\s*\(([\s\S]*)\)$/);
  if (randMatch) {
    const args = splitCallArgs(randMatch[1]);
    return {
      type: "operator_random",
      from: decomposeExpression(args[0] || "1"),
      to: decomposeExpression(args[1] || "10")
    };
  }

  // JS Random: Math.floor(Math.random() * (b - a + 1)) + a
  const jsRandMatch = str.match(/^Math\.floor\s*\(\s*Math\.random\s*\(\s*\)\s*\*\s*\(([^)]+)\)\s*\)\s*\+\s*(.+)$/);
  if (jsRandMatch) {
    const innerTo = jsRandMatch[1].trim();
    const innerFrom = jsRandMatch[2].trim();
    return {
      type: "operator_random",
      from: decomposeExpression(innerFrom),
      to: decomposeExpression(innerTo.replace(/\s*-\s*.*$/, "").trim())
    };
  }

  // 11. Sensing reporters & booleans
  if (str === "user_answer" || str === "answer") {
    return { type: "sensing_answer" };
  }
  if (str === "mouse_x" || str === "mouse.x") {
    return { type: "sensing_mousex" };
  }
  if (str === "mouse_y" || str === "mouse.y") {
    return { type: "sensing_mousey" };
  }
  if (str === "mouse_down" || str === "mouse.isDown") {
    return { type: "sensing_mousedown" };
  }
  if (str === "timer" || str === "time.time() - timer_start" || str === "(Date.now() - timerStart) / 1000") {
    return { type: "sensing_timer" };
  }
  if (str === "microphone.loudness") {
    return { type: "sensing_loudness" };
  }

  const keyMatch = str.match(/^(?:keyboard\.(?:is_pressed|isPressed))\s*\(([\s\S]*)\)$/);
  if (keyMatch) {
    const keyArg = splitCallArgs(keyMatch[1])[0] || "space";
    const cleanKey = keyArg.replace(/["']/g, "").trim();
    return { type: "sensing_keypressed", key: cleanKey };
  }

  const touchingMatch = str.match(/^(?:([a-zA-Z0-9_]+)\.)?(?:is_touching|isTouching|touching_object|touchingObject|touching)\s*\(([\s\S]*)\)$/);
  if (touchingMatch) {
    const objArg = splitCallArgs(touchingMatch[2])[0] || "mouse-pointer";
    const cleanObj = objArg.replace(/["']/g, "").trim();
    return { type: "sensing_touchingobject", target: toScratchMenuValue("sensing_touchingobjectmenu", cleanObj) };
  }

  const touchColMatch = str.match(/^(?:([a-zA-Z0-9_]+)\.)?(?:touching_color|touchingColor|is_touching_color|isTouchingColor)\s*\(([\s\S]*)\)$/);
  if (touchColMatch) {
    const colArg = splitCallArgs(touchColMatch[2])[0] || '"#62b8c3"';
    return { type: "sensing_touchingcolor", color: sanitizeHexColor(colArg) };
  }

  const colTouchColMatch = str.match(/^(?:([a-zA-Z0-9_]+)\.)?(?:color_is_touching_color|colorIsTouchingColor)\s*\(([\s\S]*)\)$/);
  if (colTouchColMatch) {
    const args = splitCallArgs(colTouchColMatch[2]);
    return {
      type: "sensing_coloristouchingcolor",
      color1: sanitizeHexColor(args[0] || '"#00ff00"'),
      color2: sanitizeHexColor(args[1] || '"#62b8c3"')
    };
  }

  const distMatch = str.match(/^(?:([a-zA-Z0-9_]+)\.)?(?:distance_to|distanceTo)\s*\(([\s\S]*)\)$/);
  if (distMatch) {
    const objArg = splitCallArgs(distMatch[2])[0] || "mouse-pointer";
    const cleanObj = objArg.replace(/["']/g, "").trim();
    return { type: "sensing_distanceto", target: toScratchMenuValue("sensing_distancetomenu", cleanObj) };
  }

  if (str === "days_since_2000" || str.includes("new Date(2000")) {
    return { type: "sensing_dayssince2000" };
  }
  if (str === "username" || str === "user.name") {
    return { type: "sensing_username" };
  }

  // Motion reporters
  const motionRepMatch = str.match(/^(?:([a-zA-Z0-9_]+)\.)?(x|y|direction)$/);
  if (motionRepMatch) {
    const prop = motionRepMatch[2];
    if (prop === "x") return { type: "motion_xposition" };
    if (prop === "y") return { type: "motion_yposition" };
    if (prop === "direction") return { type: "motion_direction" };
  }

  // Looks reporters
  const looksSizeMatch = str.match(/^(?:([a-zA-Z0-9_]+)\.)?size$/);
  if (looksSizeMatch) {
    return { type: "looks_size" };
  }
  const costumeMatch = str.match(/^(?:([a-zA-Z0-9_]+)\.)?costume[._](number|name)$/);
  if (costumeMatch) {
    return { type: "looks_costumenumbername", numberName: costumeMatch[2] };
  }
  const backdropMatch = str.match(/^(?:([a-zA-Z0-9_]+)\.)?backdrop[._](number|name)$/);
  if (backdropMatch) {
    return { type: "looks_backdropnumbername", numberName: backdropMatch[2] };
  }

  // Length: len(...) or .length
  const lenMatch = str.match(/^len\s*\(([\s\S]*)\)$/) || str.match(/^([a-zA-Z0-9_]+)\.length$/);
  if (lenMatch) {
    const inner = lenMatch[1];
    return {
      type: "operator_length",
      str: decomposeExpression(inner)
    };
  }

  // Indexing: str[idx] or list[idx]
  const indexMatch = str.match(/^([a-zA-Z0-9_]+|["'].*?["'])\s*\[\s*(?:int\()?(.*?)(?:\)\s*)?\s*\]$/);
  if (indexMatch) {
    const targetExpr = indexMatch[1];
    let idxExpr = indexMatch[2].trim();
    const minusOneMatch = idxExpr.match(/^(.*?)\s*-\s*1$/);
    if (minusOneMatch) {
      idxExpr = minusOneMatch[1].trim();
    }
    return {
      type: "operator_letter_of",
      letter: decomposeExpression(idxExpr),
      str: decomposeExpression(targetExpr)
    };
  }

  // 12. Math library: round(), abs(), math.floor(), math.ceil(), math.sqrt(), etc.
  const roundMatch = str.match(/^(?:round|Math\.round)\s*\(([\s\S]*)\)$/);
  if (roundMatch) {
    return { type: "operator_round", num: decomposeExpression(roundMatch[1]) };
  }

  const mathOpMatch = str.match(/^(?:math\.|Math\.)?(floor|ceil|ceiling|sqrt|sin|cos|tan|asin|acos|atan|log|log10|exp|abs)\s*\(([\s\S]*)\)$/);
  if (mathOpMatch) {
    let fn = mathOpMatch[1];
    if (fn === "ceil") fn = "ceiling";
    return {
      type: "operator_mathop",
      operator: fn,
      num: decomposeExpression(mathOpMatch[2])
    };
  }

  // 13. Template strings f"..." or `...`
  if (/^f["']/.test(str) || /^`/.test(str)) {
    let inner = str.replace(/^f["']/, '').replace(/["']$/, '').replace(/^`/, '').replace(/`$/, '');
    const singleBraceMatch = inner.match(/^(.*?)(?:\{|\$\{)\s*([a-zA-Z0-9_]+)\s*\}(.*?)$/);
    if (singleBraceMatch) {
      const prefix = singleBraceMatch[1];
      const varName = singleBraceMatch[2];
      const suffix = singleBraceMatch[3];
      const varPart = varName === "user_answer" || varName === "answer"
        ? { type: "sensing_answer" }
        : { type: "data_variable", varName };
      if (prefix && !suffix) {
        return { type: "operator_join", left: { type: "primitive", val: prefix }, right: varPart };
      } else if (!prefix && suffix) {
        return { type: "operator_join", left: varPart, right: { type: "primitive", val: suffix } };
      } else if (prefix && suffix) {
        return {
          type: "operator_join",
          left: { type: "primitive", val: prefix },
          right: { type: "operator_join", left: varPart, right: { type: "primitive", val: suffix } }
        };
      } else {
        return varPart;
      }
    }
  }

  // 14. Variable identifier
  if (/^[a-zA-Z_]\w*$/.test(str)) {
    return { type: "data_variable", varName: str };
  }

  return { type: "primitive", val: str };
};

const decomposeMessageExpr = decomposeExpression;

// Helper to safely set Scratch primitive block input
const setPrimitiveInput = (block, inputName, newVal, blocksContainer = null, explicitPrimType = null) => {
  if (!block || !block.inputs) return false;
  const strVal = String(newVal);

  if (block.inputs[inputName]) {
    const inputArr = block.inputs[inputName];
    // Scratch input array structure: [1, [primitiveType, value]]
    if (Array.isArray(inputArr) && inputArr.length >= 2) {
      if (Array.isArray(inputArr[1]) && inputArr[1].length >= 2) {
        const oldVal = inputArr[1][1];
        if (explicitPrimType !== null && explicitPrimType !== undefined) {
          inputArr[1][0] = explicitPrimType;
        }
        if (String(oldVal) !== strVal) {
          inputArr[1][1] = strVal;
          return { oldVal, newVal: strVal };
        }
        return false;
      }
      // Scratch input referencing shadow block ID: [1, "shadowId"]
      if (typeof inputArr[1] === "string" && blocksContainer && blocksContainer[inputArr[1]]) {
        const shadowBlock = blocksContainer[inputArr[1]];
        if (shadowBlock.fields) {
          const fieldKey = Object.keys(shadowBlock.fields)[0] || (explicitPrimType === 9 ? "COLOUR" : "NUM");
          const f = shadowBlock.fields[fieldKey];
          let oldVal = null;
          if (Array.isArray(f)) {
            oldVal = f[0];
            if (String(oldVal) !== strVal) {
              f[0] = strVal;
              return { oldVal, newVal: strVal };
            }
            return false;
          } else if (f && typeof f === "object") {
            oldVal = f.value;
            if (String(oldVal) !== strVal) {
              f.value = strVal;
              return { oldVal, newVal: strVal };
            }
            return false;
          }
        }
      }
    } else if (typeof inputArr === "object" && !Array.isArray(inputArr)) {
      // VM object structure: { name: "STEPS", block: "shadowId", shadow: "shadowId" }
      const shadowId = inputArr.block || inputArr.shadow;
      if (shadowId && blocksContainer && blocksContainer[shadowId]) {
        const shadowBlock = blocksContainer[shadowId];
        if (shadowBlock.fields) {
          const fieldKey = Object.keys(shadowBlock.fields)[0] || (explicitPrimType === 9 ? "COLOUR" : "NUM");
          const f = shadowBlock.fields[fieldKey];
          let oldVal = null;
          if (Array.isArray(f)) {
            oldVal = f[0];
            if (String(oldVal) !== strVal) {
              f[0] = strVal;
              return { oldVal, newVal: strVal };
            }
            return false;
          } else if (f && typeof f === "object") {
            oldVal = f.value;
            if (String(oldVal) !== strVal) {
              f.value = strVal;
              return { oldVal, newVal: strVal };
            }
            return false;
          }
        }
      }
    }
  }

  const primType = explicitPrimType !== null && explicitPrimType !== undefined
    ? explicitPrimType
    : (isNaN(Number(newVal)) ? 10 : 4);
  block.inputs[inputName] = [1, [primType, strVal]];
  return { oldVal: null, newVal: strVal };
};

const applyDecomposedInput = (blocks, parentBlock, inputName, decomposed, projectObj, target, isBooleanSlot = false, procContext = null) => {
  if (!decomposed || !parentBlock) return;

  const currInput = parentBlock.inputs?.[inputName];
  let currChildId = null;
  if (Array.isArray(currInput)) {
    currChildId = (typeof currInput[1] === "string") ? currInput[1] : null;
  } else if (currInput && typeof currInput === "object") {
    currChildId = currInput.block || currInput.shadow || null;
  }

  if (decomposed.type === "primitive") {
    if (currChildId && blocks[currChildId] && blocks[currChildId].parent === parentBlock.id && !blocks[currChildId].shadow) {
      deleteBlockTree(blocks, currChildId);
    }
    setPrimitiveInput(parentBlock, inputName, decomposed.val, blocks);
    return;
  }

  let opcode = decomposed.type;
  let procArg = null;
  if (opcode === "data_variable" && procContext && procContext.paramMap) {
    const vName = decomposed.varName || "";
    procArg = procContext.paramMap[vName.toLowerCase()] ||
              procContext.paramMap[vName.replace(/_+/g, "_").toLowerCase()];
    if (procArg) {
      opcode = (isBooleanSlot || procArg.isBoolean) ? "argument_reporter_boolean" : "argument_reporter_string_number";
    }
  }

  let childBlockId = null;

  if (currChildId && blocks[currChildId] && blocks[currChildId].opcode === opcode) {
    childBlockId = currChildId;
    blocks[childBlockId].parent = parentBlock.id;
  } else {
    if (currChildId && blocks[currChildId] && blocks[currChildId].parent === parentBlock.id && !blocks[currChildId].shadow) {
      deleteBlockTree(blocks, currChildId);
    }
    childBlockId = generateBlockId();
    blocks[childBlockId] = {
      id: childBlockId,
      opcode,
      next: null,
      parent: parentBlock.id,
      inputs: {},
      fields: {},
      shadow: false,
      topLevel: false,
    };
  }

  if (!parentBlock.inputs) parentBlock.inputs = {};
  if (isBooleanSlot) {
    parentBlock.inputs[inputName] = [2, childBlockId];
  } else {
    const defaultShadowVal = (opcode === "operator_join" || opcode === "data_variable" || opcode === "argument_reporter_string_number" || opcode === "argument_reporter_boolean") ? [10, ""] : [4, "10"];
    parentBlock.inputs[inputName] = [3, childBlockId, defaultShadowVal];
  }

  const childBlock = blocks[childBlockId];

  if (procArg) {
    childBlock.fields = { VALUE: [procArg.originalName, null] };
  } else if (opcode === "operator_random") {
    applyDecomposedInput(blocks, childBlock, "FROM", decomposed.from, projectObj, target, false, procContext);
    applyDecomposedInput(blocks, childBlock, "TO", decomposed.to, projectObj, target, false, procContext);
  } else if (opcode === "operator_add" || opcode === "operator_subtract" || opcode === "operator_multiply" || opcode === "operator_divide" || opcode === "operator_mod") {
    applyDecomposedInput(blocks, childBlock, "NUM1", decomposed.left, projectObj, target, false, procContext);
    applyDecomposedInput(blocks, childBlock, "NUM2", decomposed.right, projectObj, target, false, procContext);
  } else if (opcode === "operator_gt" || opcode === "operator_lt" || opcode === "operator_equals") {
    applyDecomposedInput(blocks, childBlock, "OPERAND1", decomposed.left, projectObj, target, false, procContext);
    applyDecomposedInput(blocks, childBlock, "OPERAND2", decomposed.right, projectObj, target, false, procContext);
  } else if (opcode === "operator_and" || opcode === "operator_or") {
    applyDecomposedInput(blocks, childBlock, "OPERAND1", decomposed.left, projectObj, target, true, procContext);
    applyDecomposedInput(blocks, childBlock, "OPERAND2", decomposed.right, projectObj, target, true, procContext);
  } else if (opcode === "operator_not") {
    applyDecomposedInput(blocks, childBlock, "OPERAND", decomposed.operand, projectObj, target, true, procContext);
  } else if (opcode === "operator_mathop") {
    childBlock.fields = { OPERATOR: [decomposed.operator || "abs", null] };
    applyDecomposedInput(blocks, childBlock, "NUM", decomposed.num, projectObj, target, false, procContext);
  } else if (opcode === "operator_round") {
    applyDecomposedInput(blocks, childBlock, "NUM", decomposed.num, projectObj, target, false, procContext);
  } else if (opcode === "operator_join") {
    applyDecomposedInput(blocks, childBlock, "STRING1", decomposed.left, projectObj, target, false, procContext);
    applyDecomposedInput(blocks, childBlock, "STRING2", decomposed.right, projectObj, target, false, procContext);
  } else if (opcode === "data_variable") {
    const varName = decomposed.varName || "my_variable";
    const varId = getVariableIdOrCreate(projectObj, target, varName);
    childBlock.fields = { VARIABLE: [varName, varId] };
  } else if (opcode === "sensing_keypressed") {
    setShadowMenuInput(blocks, childBlock, "KEY_OPTION", "sensing_keyoptions", "KEY_OPTION", toScratchMenuValue("sensing_keyoptions", decomposed.key || "space"));
  } else if (opcode === "sensing_touchingobject") {
    setShadowMenuInput(blocks, childBlock, "TOUCHINGOBJECTMENU", "sensing_touchingobjectmenu", "TOUCHINGOBJECTMENU", toScratchMenuValue("sensing_touchingobjectmenu", decomposed.target || "_mouse_"));
  } else if (opcode === "operator_length") {
    applyDecomposedInput(blocks, childBlock, "STRING", decomposed.str, projectObj, target, false, procContext);
  } else if (opcode === "operator_letter_of") {
    applyDecomposedInput(blocks, childBlock, "LETTER", decomposed.letter, projectObj, target, false, procContext);
    applyDecomposedInput(blocks, childBlock, "STRING", decomposed.str, projectObj, target, false, procContext);
  } else if (opcode === "operator_contains") {
    applyDecomposedInput(blocks, childBlock, "STRING1", decomposed.left, projectObj, target, false, procContext);
    applyDecomposedInput(blocks, childBlock, "STRING2", decomposed.right, projectObj, target, false, procContext);
  } else if (opcode === "sensing_touchingcolor") {
    setPrimitiveInput(childBlock, "COLOR", sanitizeHexColor(decomposed.color || "#62b8c3"), blocks, 9);
  } else if (opcode === "sensing_coloristouchingcolor") {
    setPrimitiveInput(childBlock, "COLOR", sanitizeHexColor(decomposed.color1 || "#00ff00"), blocks, 9);
    setPrimitiveInput(childBlock, "COLOR2", sanitizeHexColor(decomposed.color2 || "#62b8c3"), blocks, 9);
  } else if (opcode === "sensing_distanceto") {
    setShadowMenuInput(blocks, childBlock, "DISTANCETOMENU", "sensing_distancetomenu", "DISTANCETOMENU", toScratchMenuValue("sensing_distancetomenu", decomposed.target || "_mouse_"));
  } else if (opcode === "looks_costumenumbername" || opcode === "looks_backdropnumbername") {
    childBlock.fields = { NUMBER_NAME: [decomposed.numberName || "number", null] };
  }
};

const applyConditionInput = (blocks, parentBlock, condStr, projectObj = null, target = null, procContext = null) => {
  if (!condStr || !parentBlock) return;
  const decomposed = decomposeExpression(condStr);
  applyDecomposedInput(blocks, parentBlock, "CONDITION", decomposed, projectObj, target, true, procContext);
};

const getChainKeys = (blocks, inputDesc) => {
  if (!inputDesc) return [];
  const headId = Array.isArray(inputDesc) ? inputDesc[1] : (inputDesc.block || inputDesc.shadow);
  if (!headId || !blocks[headId]) return [];
  const chain = [];
  let curr = headId;
  const visited = new Set();
  while (curr && blocks[curr] && !visited.has(curr)) {
    visited.add(curr);
    chain.push(curr);
    curr = blocks[curr].next;
  }
  return chain;
};

// Helper to build a new Scratch block object with primitive inputs
const buildNewBlock = (opcode, inputsMap = {}, fieldsMap = {}) => {
  const blockId = generateBlockId();
  const formattedInputs = {};

  for (const [key, val] of Object.entries(inputsMap)) {
    if (Array.isArray(val)) {
      formattedInputs[key] = val;
    } else {
      const primType = isNaN(Number(val)) ? 10 : 4;
      formattedInputs[key] = [1, [primType, String(val)]];
    }
  }

  const formattedFields = {};
  for (const [key, val] of Object.entries(fieldsMap)) {
    formattedFields[key] = Array.isArray(val) ? val : [String(val), null];
  }

  return {
    id: blockId,
    opcode,
    next: null,
    parent: null,
    inputs: formattedInputs,
    fields: formattedFields,
    shadow: false,
    topLevel: false,
    x: 0,
    y: 0,
  };
};

const applyStandaloneReporterToBlock = (blocks, b, decomposed, projectObj, target, procContext = null) => {
  if (!b || !decomposed) return;
  const opcode = b.opcode;
  if (opcode === "sensing_touchingobject") {
    setShadowMenuInput(blocks, b, "TOUCHINGOBJECTMENU", "sensing_touchingobjectmenu", "TOUCHINGOBJECTMENU", toScratchMenuValue("sensing_touchingobjectmenu", decomposed.target || "_mouse_"));
  } else if (opcode === "sensing_touchingcolor") {
    setPrimitiveInput(b, "COLOR", sanitizeHexColor(decomposed.color || "#62b8c3"), blocks, 9);
  } else if (opcode === "sensing_coloristouchingcolor") {
    setPrimitiveInput(b, "COLOR", sanitizeHexColor(decomposed.color1 || "#00ff00"), blocks, 9);
    setPrimitiveInput(b, "COLOR2", sanitizeHexColor(decomposed.color2 || "#62b8c3"), blocks, 9);
  } else if (opcode === "sensing_distanceto") {
    setShadowMenuInput(blocks, b, "DISTANCETOMENU", "sensing_distancetomenu", "DISTANCETOMENU", toScratchMenuValue("sensing_distancetomenu", decomposed.target || "_mouse_"));
  } else if (opcode === "sensing_keypressed") {
    setShadowMenuInput(blocks, b, "KEY_OPTION", "sensing_keyoptions", "KEY_OPTION", toScratchMenuValue("sensing_keyoptions", decomposed.key || "space"));
  } else if (opcode === "looks_costumenumbername" || opcode === "looks_backdropnumbername") {
    b.fields = { NUMBER_NAME: [decomposed.numberName || "number", null] };
  } else if (opcode === "operator_add" || opcode === "operator_subtract" || opcode === "operator_multiply" || opcode === "operator_divide") {
    applyDecomposedInput(blocks, b, "NUM1", decomposed.left, projectObj, target, false, procContext);
    applyDecomposedInput(blocks, b, "NUM2", decomposed.right, projectObj, target, false, procContext);
  } else if (opcode === "operator_random") {
    applyDecomposedInput(blocks, b, "FROM", decomposed.from, projectObj, target, false, procContext);
    applyDecomposedInput(blocks, b, "TO", decomposed.to, projectObj, target, false, procContext);
  } else if (opcode === "operator_lt" || opcode === "operator_equals" || opcode === "operator_gt") {
    applyDecomposedInput(blocks, b, "OPERAND1", decomposed.left, projectObj, target, false, procContext);
    applyDecomposedInput(blocks, b, "OPERAND2", decomposed.right, projectObj, target, false, procContext);
  } else if (opcode === "operator_and" || opcode === "operator_or") {
    applyDecomposedInput(blocks, b, "OPERAND1", decomposed.left, projectObj, target, true, procContext);
    applyDecomposedInput(blocks, b, "OPERAND2", decomposed.right, projectObj, target, true, procContext);
  } else if (opcode === "operator_not") {
    applyDecomposedInput(blocks, b, "OPERAND", decomposed.operand, projectObj, target, true, procContext);
  }
};

const applyStmtToBlock = (blocks, b, stmt, projectObj, target, logCtx, procContext = null) => {
  const spriteName = logCtx?.spriteName || target.name || "Sprite1";
  let mutation = null;

  if (stmt.type === "motion_movesteps") {
    applyDecomposedInput(blocks, b, "STEPS", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "steps", newVal: stmt.val };
  } else if (stmt.type === "motion_turnright" || stmt.type === "motion_turnleft") {
    applyDecomposedInput(blocks, b, "DEGREES", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "degrees", newVal: stmt.val };
  } else if (stmt.type === "motion_gotoxy") {
    applyDecomposedInput(blocks, b, "X", decomposeExpression(stmt.x), projectObj, target, false, procContext);
    applyDecomposedInput(blocks, b, "Y", decomposeExpression(stmt.y), projectObj, target, false, procContext);
    mutation = { oldVal: "pos", newVal: `(${stmt.x}, ${stmt.y})` };
  } else if (stmt.type === "motion_goto") {
    const toVal = toScratchMenuValue("motion_goto_menu", stmt.val || "_random_");
    mutation = setShadowMenuInput(blocks, b, "TO", "motion_goto_menu", "TO", toVal);
  } else if (stmt.type === "motion_glideto") {
    applyDecomposedInput(blocks, b, "SECS", decomposeExpression(stmt.secs), projectObj, target, false, procContext);
    const toVal = toScratchMenuValue("motion_glideto_menu", stmt.val || "_random_");
    mutation = setShadowMenuInput(blocks, b, "TO", "motion_glideto_menu", "TO", toVal);
  } else if (stmt.type === "motion_glidesecstoxy") {
    applyDecomposedInput(blocks, b, "SECS", decomposeExpression(stmt.secs), projectObj, target, false, procContext);
    applyDecomposedInput(blocks, b, "X", decomposeExpression(stmt.x), projectObj, target, false, procContext);
    applyDecomposedInput(blocks, b, "Y", decomposeExpression(stmt.y), projectObj, target, false, procContext);
    mutation = { oldVal: "glide", newVal: `(${stmt.x}, ${stmt.y}) in ${stmt.secs}s` };
  } else if (stmt.type === "motion_pointtowards") {
    const towardsVal = toScratchMenuValue("motion_pointtowards_menu", stmt.val || "_mouse_");
    mutation = setShadowMenuInput(blocks, b, "TOWARDS", "motion_pointtowards_menu", "TOWARDS", towardsVal);
  } else if (stmt.type === "motion_ifonedgebounce") {
    mutation = { oldVal: "motion", newVal: "bounce_if_on_edge" };
  } else if (stmt.type === "motion_setrotationstyle") {
    b.fields = { STYLE: [stmt.val || "all around", null] };
    mutation = { oldVal: "style", newVal: stmt.val || "all around" };
  } else if (stmt.type === "motion_changexby") {
    applyDecomposedInput(blocks, b, "DX", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "dx", newVal: stmt.val };
  } else if (stmt.type === "motion_changeyby") {
    applyDecomposedInput(blocks, b, "DY", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "dy", newVal: stmt.val };
  } else if (stmt.type === "motion_setx") {
    applyDecomposedInput(blocks, b, "X", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "x", newVal: stmt.val };
  } else if (stmt.type === "motion_sety") {
    applyDecomposedInput(blocks, b, "Y", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "y", newVal: stmt.val };
  } else if (stmt.type === "motion_pointindirection") {
    applyDecomposedInput(blocks, b, "DIRECTION", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "dir", newVal: stmt.val };
  } else if (stmt.type === "looks_sayforsecs" || stmt.type === "looks_thinkforsecs") {
    if (stmt.secs !== null && stmt.secs !== undefined) {
      applyDecomposedInput(blocks, b, "SECS", decomposeExpression(stmt.secs), projectObj, target, false, procContext);
    }
    applyDecomposedInput(blocks, b, "MESSAGE", stmt.decomposedMsg || decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "msg", newVal: stmt.val };
  } else if (stmt.type === "looks_say" || stmt.type === "looks_think") {
    if (b.inputs && b.inputs.SECS) delete b.inputs.SECS;
    applyDecomposedInput(blocks, b, "MESSAGE", stmt.decomposedMsg || decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "msg", newVal: stmt.val };
  } else if (stmt.type === "looks_show" || stmt.type === "looks_hide" || stmt.type === "looks_nextcostume" || stmt.type === "looks_nextbackdrop") {
    mutation = { oldVal: b.opcode, newVal: stmt.type };
  } else if (stmt.type === "looks_switchcostumeto") {
    mutation = setShadowMenuInput(blocks, b, "COSTUME", "looks_costume", "COSTUME", stmt.val);
  } else if (stmt.type === "looks_switchbackdropto") {
    mutation = setShadowMenuInput(blocks, b, "BACKDROP", "looks_backdrops", "BACKDROP", stmt.val);
  } else if (stmt.type === "looks_changesizeby") {
    applyDecomposedInput(blocks, b, "CHANGE", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "size", newVal: stmt.val };
  } else if (stmt.type === "looks_setsizeto") {
    applyDecomposedInput(blocks, b, "SIZE", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "size", newVal: stmt.val };
  } else if (stmt.type === "looks_changeeffectby") {
    b.fields = { EFFECT: [(stmt.effect || "color").toUpperCase(), null] };
    applyDecomposedInput(blocks, b, "CHANGE", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "effect", newVal: `${stmt.effect} + ${stmt.val}` };
  } else if (stmt.type === "looks_seteffectto") {
    b.fields = { EFFECT: [(stmt.effect || "color").toUpperCase(), null] };
    applyDecomposedInput(blocks, b, "VALUE", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "effect", newVal: `${stmt.effect} = ${stmt.val}` };
  } else if (stmt.type === "looks_cleargraphiceffects") {
    mutation = { oldVal: "effects", newVal: "cleared" };
  } else if (stmt.type === "looks_gotofrontback") {
    b.fields = { FRONT_BACK: [stmt.val || "front", null] };
    mutation = { oldVal: "layer", newVal: stmt.val || "front" };
  } else if (stmt.type === "looks_goforwardbackwardlayers") {
    b.fields = { FORWARD_BACKWARD: [stmt.dir || "forward", null] };
    applyDecomposedInput(blocks, b, "NUM", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "layers", newVal: `${stmt.dir} ${stmt.val}` };
  } else if (stmt.type === "control_wait") {
    applyDecomposedInput(blocks, b, "DURATION", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "wait", newVal: stmt.val };
  } else if (stmt.type === "control_wait_until") {
    if (stmt.cond) applyConditionInput(blocks, b, stmt.cond, projectObj, target, procContext);
    mutation = { oldVal: "wait", newVal: `until ${stmt.cond}` };
  } else if (stmt.type === "control_create_clone_of") {
    const cloneOpt = toScratchMenuValue("control_create_clone_of_menu", stmt.val || "_myself_");
    mutation = setShadowMenuInput(blocks, b, "CLONE_OPTION", "control_create_clone_of_menu", "CLONE_OPTION", cloneOpt);
  } else if (stmt.type === "control_delete_this_clone") {
    mutation = { oldVal: b.opcode, newVal: "delete_this_clone" };
  } else if (stmt.type === "control_stop") {
    b.fields = { STOP_OPTION: [stmt.val || "all", null] };
    mutation = { oldVal: "running", newVal: stmt.val || "stop all" };
  } else if (stmt.type === "control_if" || stmt.type === "control_if_else") {
    if (stmt.cond) applyConditionInput(blocks, b, stmt.cond, projectObj, target, procContext);
    const subKeys1 = getChainKeys(blocks, b.inputs?.SUBSTACK);
    const head1 = reconcileBlockChain(blocks, subKeys1, stmt.subStatements || [], projectObj, target, b.id, "SUBSTACK", logCtx, procContext);
    if (head1) {
      b.inputs.SUBSTACK = [2, head1];
      blocks[head1].parent = b.id;
    } else if (b.inputs) {
      delete b.inputs.SUBSTACK;
    }
    if (stmt.type === "control_if_else") {
      const subKeys2 = getChainKeys(blocks, b.inputs?.SUBSTACK2);
      const head2 = reconcileBlockChain(blocks, subKeys2, stmt.subStatements2 || [], projectObj, target, b.id, "SUBSTACK2", logCtx, procContext);
      if (head2) {
        b.inputs.SUBSTACK2 = [2, head2];
        blocks[head2].parent = b.id;
      } else if (b.inputs) {
        delete b.inputs.SUBSTACK2;
      }
    }
  } else if (stmt.type === "control_repeat") {
    applyDecomposedInput(blocks, b, "TIMES", decomposeExpression(stmt.times), projectObj, target, false, procContext);
    const subKeys = getChainKeys(blocks, b.inputs?.SUBSTACK);
    const head = reconcileBlockChain(blocks, subKeys, stmt.subStatements || [], projectObj, target, b.id, "SUBSTACK", logCtx, procContext);
    if (head) {
      b.inputs.SUBSTACK = [2, head];
      blocks[head].parent = b.id;
    } else if (b.inputs) {
      delete b.inputs.SUBSTACK;
    }
  } else if (stmt.type === "control_forever" || stmt.type === "control_repeat_until") {
    if (stmt.type === "control_repeat_until" && stmt.cond) applyConditionInput(blocks, b, stmt.cond, projectObj, target, procContext);
    const subKeys = getChainKeys(blocks, b.inputs?.SUBSTACK);
    const head = reconcileBlockChain(blocks, subKeys, stmt.subStatements || [], projectObj, target, b.id, "SUBSTACK", logCtx, procContext);
    if (head) {
      b.inputs.SUBSTACK = [2, head];
      blocks[head].parent = b.id;
    } else if (b.inputs) {
      delete b.inputs.SUBSTACK;
    }
  } else if (stmt.type === "data_setvariableto" || stmt.type === "data_changevariableby") {
    applyDecomposedInput(blocks, b, "VALUE", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    const varId = (b.fields && b.fields.VARIABLE && b.fields.VARIABLE[1]) || getVariableIdOrCreate(projectObj, target, stmt.varName);
    b.fields = { VARIABLE: [stmt.varName, varId] };
    mutation = { oldVal: "var", newVal: stmt.val };
  } else if (stmt.type === "data_addtolist") {
    applyDecomposedInput(blocks, b, "ITEM", decomposeExpression(stmt.item), projectObj, target, false, procContext);
    const listId = (b.fields && b.fields.LIST && b.fields.LIST[1]) || getListIdOrCreate(projectObj, target, stmt.listName);
    b.fields = { LIST: [stmt.listName, listId] };
    mutation = { oldVal: "list", newVal: stmt.item };
  } else if (stmt.type === "data_deleteoflist") {
    mutation = setPrimitiveInput(b, "INDEX", stmt.idx, blocks);
    const listId = (b.fields && b.fields.LIST && b.fields.LIST[1]) || getListIdOrCreate(projectObj, target, stmt.listName);
    b.fields = { LIST: [stmt.listName, listId] };
  } else if (stmt.type === "data_deletealloflist") {
    const listId = (b.fields && b.fields.LIST && b.fields.LIST[1]) || getListIdOrCreate(projectObj, target, stmt.listName);
    b.fields = { LIST: [stmt.listName, listId] };
    mutation = { oldVal: "all", newVal: "cleared" };
  } else if (stmt.type === "data_insertatlist") {
    const mIdx = setPrimitiveInput(b, "INDEX", stmt.idx, blocks);
    const mItem = setPrimitiveInput(b, "ITEM", stmt.item, blocks);
    const listId = (b.fields && b.fields.LIST && b.fields.LIST[1]) || getListIdOrCreate(projectObj, target, stmt.listName);
    b.fields = { LIST: [stmt.listName, listId] };
    if (mIdx || mItem) mutation = { oldVal: stmt.listName, newVal: `inserted ${stmt.item} at ${stmt.idx}` };
  } else if (stmt.type === "data_replaceitemoflist") {
    const mIdx = setPrimitiveInput(b, "INDEX", stmt.idx, blocks);
    const mItem = setPrimitiveInput(b, "ITEM", stmt.item, blocks);
    const listId = (b.fields && b.fields.LIST && b.fields.LIST[1]) || getListIdOrCreate(projectObj, target, stmt.listName);
    b.fields = { LIST: [stmt.listName, listId] };
    if (mIdx || mItem) mutation = { oldVal: stmt.listName, newVal: `replaced item ${stmt.idx} with ${stmt.item}` };
  } else if (stmt.type === "data_showlist" || stmt.type === "data_hidelist") {
    const listId = (b.fields && b.fields.LIST && b.fields.LIST[1]) || getListIdOrCreate(projectObj, target, stmt.listName);
    b.fields = { LIST: [stmt.listName, listId] };
    mutation = { oldVal: "list", newVal: stmt.type === "data_showlist" ? "shown" : "hidden" };
  } else if (stmt.type === "data_showvariable" || stmt.type === "data_hidevariable") {
    const varId = (b.fields && b.fields.VARIABLE && b.fields.VARIABLE[1]) || getVariableIdOrCreate(projectObj, target, stmt.varName);
    b.fields = { VARIABLE: [stmt.varName, varId] };
    mutation = { oldVal: "variable", newVal: stmt.type === "data_showvariable" ? "shown" : "hidden" };
  } else if (stmt.type === "teachableMachine_loadModelUrl") {
    const mUrl = setPrimitiveInput(b, "URL", stmt.urlVal, blocks);
    const mType = setShadowMenuInput(blocks, b, "TYPE", "teachableMachine_menu_modelTypeMenu", "modelTypeMenu", stmt.typeVal || "image");
    if (mUrl || mType) mutation = { oldVal: mUrl?.oldVal || "url", newVal: `${stmt.urlVal} (${stmt.typeVal || "image"})` };
  } else if (stmt.type === "teachableMachine_setVideoState") {
    mutation = setShadowMenuInput(blocks, b, "VIDEO_STATE", "teachableMachine_menu_videoStateMenu", "videoStateMenu", stmt.val || "on");
  } else if (stmt.type === "teachableMachine_setVideoTransparency") {
    mutation = setPrimitiveInput(b, "TRANSPARENCY", stmt.val, blocks);
  } else if (stmt.type === "teachableMachine_setConfidenceThreshold") {
    mutation = setPrimitiveInput(b, "THRESHOLD", stmt.val, blocks);
  } else if (stmt.type === "sensing_askandwait") {
    mutation = setPrimitiveInput(b, "QUESTION", stmt.val, blocks);
  } else if (stmt.type === "event_broadcast" || stmt.type === "event_broadcastandwait") {
    const bcastId = getBroadcastIdOrCreate(projectObj, stmt.val);
    b.inputs = { BROADCAST_INPUT: [1, [11, stmt.val, bcastId]] };
    mutation = { oldVal: "broadcast", newVal: stmt.val };
  } else if (stmt.type === "sound_play" || stmt.type === "sound_playuntildone") {
    mutation = setShadowMenuInput(blocks, b, "SOUND_MENU", "sound_sounds_menu", "SOUND_MENU", stmt.val || "Meow");
  } else if (stmt.type === "sound_stopallsounds") {
    mutation = { oldVal: "playing", newVal: "stopped" };
  } else if (stmt.type === "sound_setvolumeto" || stmt.type === "sound_changevolumeby") {
    applyDecomposedInput(blocks, b, "VOLUME", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "volume", newVal: stmt.val };
  } else if (stmt.type === "sound_seteffectto" || stmt.type === "sound_changeeffectby") {
    b.fields = { EFFECT: [(stmt.effect || "pitch").toUpperCase(), null] };
    applyDecomposedInput(blocks, b, "VALUE", decomposeExpression(stmt.val), projectObj, target, false, procContext);
    mutation = { oldVal: "sound_effect", newVal: `${stmt.effect} = ${stmt.val}` };
  } else if (stmt.type === "sound_cleareffects") {
    mutation = { oldVal: "sound_effects", newVal: "cleared" };
  } else if (stmt.type === "procedures_call") {
    let foundMutation = null;
    projectObj.targets.forEach((t) => {
      if (t.blocks) {
        Object.values(t.blocks).forEach((otherB) => {
          if (otherB.opcode === "procedures_prototype" && otherB.mutation) {
            const pc = (otherB.mutation.proccode || "").replace(/%[snb]/g, "").trim().replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
            if (pc.replace(/_+/g, "_") === stmt.procName.replace(/_+/g, "_")) foundMutation = otherB.mutation;
          }
        });
      }
    });
    if (foundMutation) {
      b.mutation = JSON.parse(JSON.stringify(foundMutation));
      try {
        const argIds = JSON.parse(foundMutation.argumentids || "[]");
        argIds.forEach((argId, aIdx) => {
          if (stmt.args && stmt.args[aIdx] !== undefined) {
            applyDecomposedInput(blocks, b, argId, decomposeExpression(stmt.args[aIdx]), projectObj, target, false, procContext);
          }
        });
      } catch (e) {}
    }
  } else if (stmt.isStandaloneReporter && stmt.decomposed) {
    applyStandaloneReporterToBlock(blocks, b, stmt.decomposed, projectObj, target, procContext);
    mutation = { oldVal: "standalone", newVal: stmt.type };
  }

  if (mutation && logCtx) {
    logCtx.mutationsCount++;
    logCtx.mutationLogs.push({
      type: "success",
      text: `Updated [${spriteName}] ${b.opcode.replace(/_/g, " ")}: ${mutation.oldVal ?? "initial"} ➔ ${mutation.newVal} (Line ${stmt.line || 1})`,
    });
  }
};

const reconcileBlockChain = (blocks, bodyKeys, stmts, projectObj, target, parentId = null, inputName = null, logCtx = null, procContext = null) => {
  let lastBlockKey = null;
  let firstBlockKey = null;

  stmts.forEach((stmt, sIdx) => {
    const currentBlockKey = bodyKeys[sIdx];

    if (stmt.type === "unparsed_fallback") {
      if (currentBlockKey && blocks[currentBlockKey]) {
        lastBlockKey = currentBlockKey;
        if (!firstBlockKey) firstBlockKey = currentBlockKey;
        if (logCtx) {
          logCtx.mutationLogs.push({
            type: "warning",
            text: `⚠️ Line ${stmt.line || 1}: Unrecognized statement "${stmt.originalLine || ""}". Preserved existing [${(blocks[currentBlockKey].opcode || "").replace(/_/g, " ")}] block.`,
          });
        }
      } else if (logCtx) {
        logCtx.mutationLogs.push({
          type: "warning",
          text: `⚠️ Line ${stmt.line || 1}: Ignored unrecognized statement "${stmt.originalLine || ""}".`,
        });
      }
      return;
    }

    if (currentBlockKey && blocks[currentBlockKey]) {
      const b = blocks[currentBlockKey];
      const prevBlockKey = lastBlockKey;
      lastBlockKey = currentBlockKey;
      if (!firstBlockKey) firstBlockKey = currentBlockKey;

      if (b.opcode !== stmt.type) {
        const oldNext = b.next;
        deleteBlockTree(blocks, currentBlockKey);
        const nb = buildNewBlock(stmt.type, stmt.inputs || {}, stmt.fields || {});
        nb.next = oldNext;
        if (prevBlockKey && blocks[prevBlockKey]) {
          blocks[prevBlockKey].next = nb.id;
          nb.parent = prevBlockKey;
        } else if (parentId) {
          nb.parent = parentId;
        }
        if (oldNext && blocks[oldNext]) {
          blocks[oldNext].parent = nb.id;
        }
        blocks[nb.id] = nb;
        applyStmtToBlock(blocks, nb, stmt, projectObj, target, logCtx, procContext);
        lastBlockKey = nb.id;
        if (sIdx === 0) firstBlockKey = nb.id;
      } else {
        applyStmtToBlock(blocks, b, stmt, projectObj, target, logCtx, procContext);
      }
    } else {
      const nb = buildNewBlock(stmt.type, stmt.inputs || {}, stmt.fields || {});
      blocks[nb.id] = nb;
      applyStmtToBlock(blocks, nb, stmt, projectObj, target, logCtx, procContext);

      if (lastBlockKey && blocks[lastBlockKey]) {
        blocks[lastBlockKey].next = nb.id;
        nb.parent = lastBlockKey;
      } else if (parentId) {
        nb.parent = parentId;
      }
      lastBlockKey = nb.id;
      if (!firstBlockKey) firstBlockKey = nb.id;

      if (logCtx) {
        logCtx.mutationsCount++;
        logCtx.mutationLogs.push({
          type: "success",
          text: `✨ Appended block [${logCtx.spriteName}] ${stmt.type.replace(/_/g, " ")} (Line ${stmt.line || 1})`,
        });
      }
    }
  });

  // PRUNE EXTRA DELETED BLOCKS FROM THIS CHAIN
  if (bodyKeys.length > stmts.length) {
    if (logCtx?.hasUnparsed) {
      if (logCtx) {
        logCtx.mutationLogs.push({
          type: "warning",
          text: `⚠️ Unparsed lines present. Preserved remaining blocks in stack to prevent accidental block deletion.`,
        });
      }
      const extraKeys = bodyKeys.slice(stmts.length);
      if (lastBlockKey && blocks[lastBlockKey] && extraKeys[0] && blocks[extraKeys[0]]) {
        blocks[lastBlockKey].next = extraKeys[0];
        blocks[extraKeys[0]].parent = lastBlockKey;
      }
    } else {
      const extraKeys = bodyKeys.slice(stmts.length);
      extraKeys.forEach((k) => {
        if (blocks[k] && blocks[k].opcode === "procedures_prototype") return;
        deleteBlockTree(blocks, k);
        if (logCtx) {
          logCtx.mutationsCount++;
          logCtx.mutationLogs.push({
            type: "info",
            text: `🗑️ Removed deleted block from [${logCtx.spriteName}]`,
          });
        }
      });
      if (lastBlockKey && blocks[lastBlockKey]) {
        blocks[lastBlockKey].next = null;
      }
    }
  }

  return firstBlockKey;
};

export const syncPythonToScratchProject = (codeText, currentProjectData, lang = "python", targetName = null) => {
  try {
    if (codeText === undefined || codeText === null || !currentProjectData) {
      return { success: false, error: "Missing code or project data" };
    }

    let projectObj = currentProjectData;
    if (typeof currentProjectData === "string") {
      try {
        projectObj = JSON.parse(currentProjectData);
      } catch (e) {
        return { success: false, error: "Invalid Scratch project JSON format" };
      }
    }

    if (!projectObj.targets || !Array.isArray(projectObj.targets)) {
      return { success: false, error: "Scratch project has no valid targets" };
    }

    // Deep clone to avoid mutating state directly
    const updatedProject = JSON.parse(JSON.stringify(projectObj));
    const mutationLogs = [];
    let mutationsCount = 0;
    const unparsedLines = [];
    const anomalies = [];

    // Determine active targets for top-level variable/list declarations (excluding runtime clones)
    const activeTargets = (targetName && targetName !== "_all_"
      ? updatedProject.targets.filter((t) => {
          if (t.isOriginal === false) return false;
          const isRequestedStage = targetName.toLowerCase() === "stage" || targetName === "_stage_";
          if (isRequestedStage) return t.isStage || t.name === "Stage";
          const sName = t.name || "Sprite1";
          const clean = sName.toLowerCase().replace(/[^a-z0-9_]/g, "_");
          return !t.isStage && (sName === targetName || clean === targetName.toLowerCase());
        })
      : updatedProject.targets.filter((t) => t.isOriginal !== false));

    // Parse statements from text and cluster them into stacks
    const lines = (codeText || "").split("\n");

    // Extract all defined procedure (My Block) names from project and from function definitions in code
    const definedProcNames = new Set();
    updatedProject.targets.forEach((t) => {
      if (t.blocks) {
        Object.values(t.blocks).forEach((b) => {
          if (b.opcode === "procedures_prototype" && b.mutation && b.mutation.proccode) {
            const raw = b.mutation.proccode.replace(/%[snb]/g, "").trim();
            const pc = raw.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
            if (pc) {
              definedProcNames.add(pc);
              definedProcNames.add(pc.replace(/_+/g, "_"));
            }
            const direct = raw.toLowerCase().replace(/\s+/g, "_");
            if (direct) definedProcNames.add(direct);
          }
        });
      }
    });
    lines.forEach((l) => {
      const t = l.trim();
      const m = t.match(/^(?:def\s+([a-zA-Z_]\w*)\s*\(|(?:async\s+)?function\s+([a-zA-Z_]\w*)\s*\()/);
      if (m) {
        const rawFn = m[1] || m[2] || "";
        const fn = rawFn.toLowerCase().replace(/_+/g, "_");
        if (fn && !fn.startsWith("when_") && !fn.startsWith("on_")) {
          definedProcNames.add(fn);
          definedProcNames.add(rawFn.toLowerCase());
        }
      }
    });

    const parsedStacks = [];
    let currentStack = { hat: null, statements: [] };
    let inFunction = false;
    let controlStack = []; // Stack of { stmt, indent, isIf, inElse }

    const finalizeCurrentStack = () => {
      controlStack = [];
      if (currentStack.statements.length > 0 || currentStack.hat) {
        parsedStacks.push(currentStack);
        currentStack = { hat: null, statements: [] };
      }
      inFunction = false;
    };

    const getActiveContainer = () => {
      if (controlStack.length === 0) return currentStack.statements;
      const top = controlStack[controlStack.length - 1];
      return top.inElse ? top.stmt.subStatements2 : top.stmt.subStatements;
    };

    lines.forEach((line, idx) => {
      const trimmed = line.trim();

      // Comments & Stack Separators
      if (!trimmed || trimmed.startsWith("#") || trimmed.startsWith("//")) {
        if (trimmed.includes("--- Script") || trimmed.includes("--- Stack") || trimmed.includes("--- Block")) {
          finalizeCurrentStack();
        } else if (!trimmed && !inFunction) {
          // A blank line outside a function indicates stack boundary
          finalizeCurrentStack();
        }
        return;
      }

      // Ignore runtime hook lines emitted by transpiler
      if (
        trimmed.includes(".when_start(") ||
        trimmed.includes(".whenStart(") ||
        trimmed.includes(".when_key_pressed(") ||
        trimmed.includes(".whenKeyPressed(") ||
        trimmed.includes(".when_clicked(") ||
        trimmed.includes(".whenClicked(") ||
        trimmed.includes(".when_greater_than(") ||
        trimmed.includes(".whenGreaterThan(") ||
        trimmed.includes(".when_backdrop_switch(") ||
        trimmed.includes(".whenBackdropSwitch(") ||
        trimmed.includes(".when_broadcast_received(") ||
        trimmed.includes(".whenBroadcastReceived(") ||
        trimmed.includes(".when_start_as_clone(") ||
        trimmed.includes(".whenStartAsClone(") ||
        trimmed.includes(".when_class_detected(") ||
        trimmed.includes(".whenClassDetected(") ||
        /^(?:when_start|on_start)(?:_\d+)?\(\);?$/.test(trimmed) ||
        /^(?:whenStart|onStart)(?:\d+)?\(\);?$/.test(trimmed) ||
        trimmed.startsWith("import ") ||
        trimmed.startsWith("from ") ||
        trimmed.startsWith("const delay =")
      ) {
        return;
      }

      // Ignore class/sprite instantiations: sprite1 = Sprite('Sprite1'), stage = Stage()
      if (trimmed.includes("= Sprite(") || trimmed.includes("= new Sprite(") || trimmed.includes("= Stage(") || trimmed.includes("= new Stage(")) {
        return;
      }

      // Check for function definitions (Event Hat blocks & Procedures)
      // 1. when_start / on_start
      const isExactFlag = trimmed.match(/^(?:def\s+(?:when_start|on_start)(?:_(\d+))?\s*\(\)|async\s+function\s+(?:whenStart|onStart)(\d+)?\s*\()/);
      const isFuzzyFlag = !isExactFlag && trimmed.match(/^(?:(?:def\w*|deff|df|async\s+def\w*|async\s+function\w*|function\w*)\s+(?:when_start|on_start|whenStart|onStart)(?:_(\d+))?\b|(?:when_start|on_start|whenStart|onStart)(?:_(\d+))?\s*(?:\(\))?\s*:)/i);
      if (isExactFlag || isFuzzyFlag) {
        finalizeCurrentStack();
        if (isFuzzyFlag) {
          unparsedLines.push({ line: idx + 1, text: trimmed, error: "Syntax error in function header" });
          mutationLogs.push({
            type: "warning",
            text: `⚠️ Line ${idx + 1}: Syntax error in function header "${trimmed}". Expected 'def when_start():'. Preserved existing [event whenflagclicked] stack.`,
          });
        }
        currentStack = { hat: { opcode: "event_whenflagclicked" }, statements: [] };
        inFunction = true;
        return;
      }

      // 2. when_key / on_key
      const isExactKey = trimmed.match(/^(?:def\s+(?:when_key_|on_key_)([a-zA-Z0-9_]+?)(?:_(\d+))?\s*\(\)|async\s+function\s+(?:whenKey_|onKey_?)([a-zA-Z0-9_]+?)(?:(\d+))?\s*\()/);
      const isFuzzyKey = !isExactKey && trimmed.match(/^(?:(?:def\w*|deff|df|async\s+def\w*|async\s+function\w*|function\w*)\s+(?:when_key_|on_key_|whenKey_|onKey_)([a-zA-Z0-9_]+?)\b|(?:when_key_|on_key_|whenKey_|onKey_)([a-zA-Z0-9_]+?)\s*(?:\(\))?\s*:)/i);
      if (isExactKey || isFuzzyKey) {
        const m = isExactKey || isFuzzyKey;
        finalizeCurrentStack();
        const rawKey = (m[1] || m[3] || "space").toLowerCase();
        if (isFuzzyKey) {
          unparsedLines.push({ line: idx + 1, text: trimmed, error: "Syntax error in function header" });
          mutationLogs.push({
            type: "warning",
            text: `⚠️ Line ${idx + 1}: Syntax error in function header "${trimmed}". Expected 'def when_key_${rawKey}():'. Preserved existing [event whenkeypressed] stack.`,
          });
        }
        currentStack = {
          hat: {
            opcode: "event_whenkeypressed",
            fields: { KEY_OPTION: [rawKey, rawKey] },
            key: rawKey,
          },
          statements: [],
        };
        inFunction = true;
        return;
      }

      // 3. when_clicked / on_click
      const isExactClick = trimmed.match(/^(?:def\s+(?:when_clicked|on_[a-zA-Z0-9_]*?_?click)(?:_(\d+))?\s*\(\)|async\s+function\s+(?:whenClicked|on[a-zA-Z0-9_]*Click)(\d+)?\s*\()/);
      const isFuzzyClick = !isExactClick && trimmed.match(/^(?:(?:def\w*|deff|df|async\s+def\w*|async\s+function\w*|function\w*)\s+(?:when_clicked|on_[a-zA-Z0-9_]*?_?click|whenClicked|on[a-zA-Z0-9_]*Click)\b|(?:when_clicked|on_[a-zA-Z0-9_]*?_?click|whenClicked|on[a-zA-Z0-9_]*Click)\s*(?:\(\))?\s*:)/i);
      if (isExactClick || isFuzzyClick) {
        finalizeCurrentStack();
        if (isFuzzyClick) {
          unparsedLines.push({ line: idx + 1, text: trimmed, error: "Syntax error in function header" });
          mutationLogs.push({
            type: "warning",
            text: `⚠️ Line ${idx + 1}: Syntax error in function header "${trimmed}". Expected 'def when_clicked():'. Preserved existing [event whenthisspriteclicked] stack.`,
          });
        }
        currentStack = { hat: { opcode: "event_whenthisspriteclicked" }, statements: [] };
        inFunction = true;
        return;
      }

      // 4. when_receive / on_receive
      const isExactBcast = trimmed.match(/^(?:def\s+(?:when_receive_|on_receive_)([a-zA-Z0-9_]+?)(?:_(\d+))?\s*\(\)|async\s+function\s+(?:whenReceive_|onReceive)([a-zA-Z0-9_]+?)(?:(\d+))?\s*\()/);
      const isFuzzyBcast = !isExactBcast && trimmed.match(/^(?:(?:def\w*|deff|df|async\s+def\w*|async\s+function\w*|function\w*)\s+(?:when_receive_|on_receive_|whenReceive_|onReceive_?)([a-zA-Z0-9_]+?)\b|(?:when_receive_|on_receive_|whenReceive_|onReceive_?)([a-zA-Z0-9_]+?)\s*(?:\(\))?\s*:)/i);
      if (isExactBcast || isFuzzyBcast) {
        const m = isExactBcast || isFuzzyBcast;
        finalizeCurrentStack();
        const rawMsg = m[1] || m[3] || "message1";
        if (isFuzzyBcast) {
          unparsedLines.push({ line: idx + 1, text: trimmed, error: "Syntax error in function header" });
          mutationLogs.push({
            type: "warning",
            text: `⚠️ Line ${idx + 1}: Syntax error in function header "${trimmed}". Expected 'def when_receive_${rawMsg}():'. Preserved existing stack.`,
          });
        }
        currentStack = {
          hat: {
            opcode: "event_whenbroadcastreceived",
            fields: { BROADCAST_OPTION: [rawMsg, rawMsg] },
            msg: rawMsg,
          },
          statements: [],
        };
        inFunction = true;
        return;
      }

      // 5. when_backdrop / on_backdrop
      const isExactBd = trimmed.match(/^(?:def\s+(?:when_backdrop_|on_backdrop_)([a-zA-Z0-9_]+?)(?:_(\d+))?\s*\(\)|async\s+function\s+(?:whenBackdrop_|onBackdrop_?)([a-zA-Z0-9_]+?)(?:(\d+))?\s*\()/);
      const isFuzzyBd = !isExactBd && trimmed.match(/^(?:(?:def\w*|deff|df|async\s+def\w*|async\s+function\w*|function\w*)\s+(?:when_backdrop_|on_backdrop_|whenBackdrop_|onBackdrop_?)([a-zA-Z0-9_]+?)\b|(?:when_backdrop_|on_backdrop_|whenBackdrop_|onBackdrop_?)([a-zA-Z0-9_]+?)\s*(?:\(\))?\s*:)/i);
      if (isExactBd || isFuzzyBd) {
        const m = isExactBd || isFuzzyBd;
        finalizeCurrentStack();
        const rawBd = m[1] || m[3] || "backdrop1";
        if (isFuzzyBd) {
          unparsedLines.push({ line: idx + 1, text: trimmed, error: "Syntax error in function header" });
          mutationLogs.push({
            type: "warning",
            text: `⚠️ Line ${idx + 1}: Syntax error in function header "${trimmed}". Expected 'def when_backdrop_${rawBd}():'. Preserved existing stack.`,
          });
        }
        currentStack = {
          hat: {
            opcode: "event_whenbackdropswitchesto",
            fields: { BACKDROP: [rawBd, rawBd] },
            backdrop: rawBd,
          },
          statements: [],
        };
        inFunction = true;
        return;
      }

      // 6. when_start_as_clone / on_clone
      const isExactClone = trimmed.match(/^(?:def\s+(?:on_start_as_clone|when_start_as_clone|on_clone)(?:_(\d+))?\s*\(\)|(?:async\s+)?function\s+(?:onStartAsClone|whenStartAsClone|onClone)(\d+)?\s*\()/);
      const isFuzzyClone = !isExactClone && trimmed.match(/^(?:(?:def\w*|deff|df|async\s+def\w*|async\s+function\w*|function\w*)\s+(?:on_start_as_clone|when_start_as_clone|on_clone|whenStartAsClone|onClone)\b|(?:on_start_as_clone|when_start_as_clone|on_clone|whenStartAsClone|onClone)\s*(?:\(\))?\s*:)/i);
      if (isExactClone || isFuzzyClone) {
        finalizeCurrentStack();
        if (isFuzzyClone) {
          unparsedLines.push({ line: idx + 1, text: trimmed, error: "Syntax error in function header" });
          mutationLogs.push({
            type: "warning",
            text: `⚠️ Line ${idx + 1}: Syntax error in function header "${trimmed}". Expected 'def when_start_as_clone():'. Preserved existing stack.`,
          });
        }
        currentStack = { hat: { opcode: "control_start_as_clone" }, statements: [] };
        inFunction = true;
        return;
      }

      // 7. on_class (Teachable Machine)
      const isExactTM = trimmed.match(/^(?:def\s+on_class_([a-zA-Z0-9_]+?)(?:_(\d+))?\s*\(\)|(?:async\s+)?function\s+onClass([a-zA-Z0-9_]+?)(?:(\d+))?\s*\()/);
      const isFuzzyTM = !isExactTM && trimmed.match(/^(?:(?:def\w*|deff|df|async\s+def\w*|async\s+function\w*|function\w*)\s+(?:on_class_|when_class_|onClass|whenClass)([a-zA-Z0-9_]+?)\b|(?:on_class_|when_class_|onClass|whenClass)([a-zA-Z0-9_]+?)\s*(?:\(\))?\s*:)/i);
      if (isExactTM || isFuzzyTM) {
        const m = isExactTM || isFuzzyTM;
        finalizeCurrentStack();
        const rawSlug = m[1] || m[3] || "Thumbs_Up";
        const readableClass = rawSlug.replace(/_/g, " ");
        if (isFuzzyTM) {
          unparsedLines.push({ line: idx + 1, text: trimmed, error: "Syntax error in function header" });
          mutationLogs.push({
            type: "warning",
            text: `⚠️ Line ${idx + 1}: Syntax error in function header "${trimmed}". Expected 'def on_class_${rawSlug}():'. Preserved existing stack.`,
          });
        }
        currentStack = {
          hat: {
            opcode: "teachableMachine_whenClassDetected",
            tmClass: readableClass,
          },
          statements: [],
        };
        inFunction = true;
        return;
      }

      // 8. when_..._greater_than
      const isExactGt = trimmed.match(/^(?:def\s+when_([a-zA-Z0-9_]+?)_greater_than_([-\d.]+)\s*\(\)|(?:async\s+)?function\s+when([a-zA-Z0-9_]+?)GreaterThan([-\d.]+)\s*\()/);
      const isFuzzyGt = !isExactGt && trimmed.match(/^(?:(?:def\w*|deff|df|async\s+def\w*|async\s+function\w*|function\w*)\s+(?:when_([a-zA-Z0-9_]+?)_greater_than_([-\d.]+)|when([a-zA-Z0-9_]+?)GreaterThan([-\d.]+))\b|(?:when_([a-zA-Z0-9_]+?)_greater_than_([-\d.]+)|when([a-zA-Z0-9_]+?)GreaterThan([-\d.]+))\s*(?:\(\))?\s*:)/i);
      if (isExactGt || isFuzzyGt) {
        const m = isExactGt || isFuzzyGt;
        finalizeCurrentStack();
        const rawElem = (m[1] || m[3] || "loudness").toUpperCase();
        const rawVal = m[2] || m[4] || "10";
        if (isFuzzyGt) {
          unparsedLines.push({ line: idx + 1, text: trimmed, error: "Syntax error in function header" });
          mutationLogs.push({
            type: "warning",
            text: `⚠️ Line ${idx + 1}: Syntax error in function header "${trimmed}". Expected 'def when_${rawElem.toLowerCase()}_greater_than_${rawVal}():'. Preserved existing stack.`,
          });
        }
        currentStack = {
          hat: {
            opcode: "event_whengreaterthan",
            fields: { WHENELEMENTMENU: [rawElem, null] },
            inputs: { VALUE: rawVal },
            elem: rawElem,
            val: rawVal,
          },
          statements: [],
        };
        inFunction = true;
        return;
      }

      // 9. Custom Block (My Block / Procedure Definition)
      const hatProcMatch = trimmed.match(/^(?:def\s+([a-zA-Z_]\w*)\s*\((.*?)\)|(?:async\s+)?function\s+([a-zA-Z_]\w*)\s*\((.*?)\)\s*\{?)/);
      const fuzzyProcMatch = !hatProcMatch && trimmed.match(/^(?:def\w*|function\w*|deff|df)\s+([a-zA-Z_]\w*)\s*(?:\((.*?)\)|:|$)/i);
      if (hatProcMatch || fuzzyProcMatch) {
        const m = hatProcMatch || fuzzyProcMatch;
        const procName = (m[1] || m[3] || "").toLowerCase();
        const rawArgs = m[2] || m[4] || "";
        const argNames = rawArgs.split(",").map((a) => a.trim()).filter(Boolean);
        finalizeCurrentStack();
        if (fuzzyProcMatch) {
          unparsedLines.push({ line: idx + 1, text: trimmed, error: "Syntax error in custom block header" });
          mutationLogs.push({
            type: "warning",
            text: `⚠️ Line ${idx + 1}: Syntax error in custom block header "${trimmed}". Expected 'def ${procName}():'. Preserved existing stack.`,
          });
        }
        currentStack = {
          hat: {
            opcode: "procedures_definition",
            procName,
            argNames,
          },
          statements: [],
        };
        inFunction = true;
        return;
      }

      const indent = getLineIndent(line);

      // Check if line returns to indent level 0 (end of function)
      if (inFunction && indent === 0) {
        finalizeCurrentStack();
      }

      // Check for orphan/unexpected indentation outside of any function or control block
      if (!inFunction && controlStack.length === 0 && indent > 0) {
        unparsedLines.push({
          line: idx + 1,
          text: trimmed,
          error: "Unexpected indent outside of function",
        });
        mutationLogs.push({
          type: "warning",
          text: `⚠️ Line ${idx + 1}: Unexpected indent outside of function "${trimmed}". Ignored to prevent block duplication.`,
        });
        return;
      }

      // Check for broken function or block header at indent 0
      if (!inFunction && controlStack.length === 0 && indent === 0) {
        const isBrokenHeader = /^(?:def\w*|deff|df|async\s+def\w*|function\w*|async\s+function\w*)\b/i.test(trimmed) || trimmed.endsWith(":");
        if (isBrokenHeader) {
          finalizeCurrentStack();
          unparsedLines.push({
            line: idx + 1,
            text: trimmed,
            error: "Broken or unrecognized function header",
          });
          mutationLogs.push({
            type: "warning",
            text: `⚠️ Line ${idx + 1}: Broken function header "${trimmed}". Preserved existing blocks.`,
          });
          return;
        }
      }

      // Ignore pass statement and global declarations in Python functions
      if (trimmed === "pass" || trimmed.startsWith("global ")) {
        return;
      }

      // Capture exact class label from tm.when_class_detected("Thumbs Up", on_class_thumbs_up)
      const tmRegMatch = trimmed.match(/^tm\.(?:when_class_detected|whenClassDetected)\s*\(\s*(["'])(.*?)\1/);
      if (tmRegMatch) {
        const exactClass = tmRegMatch[2];
        const lastStack = parsedStacks.length > 0 ? parsedStacks[parsedStacks.length - 1] : null;
        if (lastStack && lastStack.hat && lastStack.hat.opcode === "teachableMachine_whenClassDetected") {
          lastStack.hat.tmClass = exactClass;
        }
        return;
      }


      // Top-level variable and list declarations outside functions
      if (!inFunction) {
        // List declaration: fruits = [] or let fruits = ["apple", "mango"];
        const listDeclMatch = trimmed.match(/^(?:let\s+|const\s+|var\s+)?([a-zA-Z_]\w*)\s*=\s*\[(.*?)\]\s*;?$/);
        if (listDeclMatch) {
          const listName = listDeclMatch[1];
          const rawItems = listDeclMatch[2].trim();
          let items = [];
          if (rawItems) {
            try {
              items = JSON.parse(`[${rawItems}]`);
            } catch (e) {
              items = rawItems.split(",").map((s) => s.trim().replace(/^['"]|['"]$/g, ""));
            }
          }
          activeTargets.forEach((t) => {
            const listId = getListIdOrCreate(updatedProject, t, listName);
            if (t.lists && t.lists[listId]) {
              t.lists[listId][1] = items;
            }
          });
          return;
        }

        // Variable declaration: score = 0 or let score = 0;
        const topVarMatch = trimmed.match(/^(?:let\s+|const\s+|var\s+|int\s+|float\s+|double\s+|auto\s+|String\s+)?([a-zA-Z_]\w*)\s*=\s*([-\d.]+|["'].*?["'])\s*;?$/);
        if (topVarMatch) {
          const rawVarName = topVarMatch[1];
          let rawVal = topVarMatch[2];
          if ((rawVal.startsWith('"') && rawVal.endsWith('"')) || (rawVal.startsWith("'") && rawVal.endsWith("'"))) {
            rawVal = rawVal.slice(1, -1);
          }
          if (!rawVal.includes("PractidoSprite") && !rawVal.includes("Sprite(") && !rawVal.includes("Stage(")) {
            const parsedVal = !isNaN(Number(rawVal)) ? Number(rawVal) : rawVal;
            activeTargets.forEach((t) => {
              const varId = getVariableIdOrCreate(updatedProject, t, rawVarName);
              if (t.variables && t.variables[varId]) {
                t.variables[varId][1] = parsedVal;
              }
            });
            return;
          }
        }
      }

      // Handle indentation unwinding for controlStack
      while (controlStack.length > 0) {
        const top = controlStack[controlStack.length - 1];
        if (indent <= top.indent) {
          const isElse = /^(?:else\s*:|\}\s*else\s*\{?)/.test(trimmed);
          if (isElse && top.isIf && !top.inElse) {
            top.stmt.type = "control_if_else";
            top.stmt.subStatements2 = [];
            top.inElse = true;
            return;
          }
          controlStack.pop();
        } else {
          break;
        }
      }

      const isElseDirect = /^(?:else\s*:|\}\s*else\s*\{?)/.test(trimmed);
      if (isElseDirect && controlStack.length > 0) {
        const top = controlStack[controlStack.length - 1];
        if (top.isIf && !top.inElse) {
          top.stmt.type = "control_if_else";
          top.stmt.subStatements2 = [];
          top.inElse = true;
          return;
        }
      }

      if (trimmed === "pass" || trimmed === "time.sleep(0.1)" || trimmed === "await delay(100);" || trimmed === "}") {
        return;
      }

      // Check for control flow statements
      const ifMatch = trimmed.match(/^if\s+(.*?)\s*:\s*$/) || trimmed.match(/^if\s*\((.*?)\)\s*\{?\s*$/);
      if (ifMatch) {
        const cond = ifMatch[1].trim();
        const stmt = {
          type: "control_if",
          cond,
          subStatements: [],
          line: idx + 1,
        };
        getActiveContainer().push(stmt);
        controlStack.push({ stmt, indent, isIf: true, inElse: false });
        return;
      }

      const repeatMatch = trimmed.match(/^(?:for\s+[a-zA-Z0-9_]+\s+in\s+range\s*\((?:int\()?\s*([^\)]+?)\s*\)?\s*\):|for\s*\(\s*(?:let|var)?\s*[a-zA-Z0-9_]+\s*=\s*0;\s*[a-zA-Z0-9_]+\s*<\s*([^\;]+?)\s*;\s*[a-zA-Z0-9_]+\+\+\s*\)\s*\{?)/);
      if (repeatMatch) {
        const times = (repeatMatch[1] || repeatMatch[2] || "10").trim();
        const stmt = {
          type: "control_repeat",
          times,
          inputs: { TIMES: times },
          subStatements: [],
          line: idx + 1,
        };
        getActiveContainer().push(stmt);
        controlStack.push({ stmt, indent, isIf: false, inElse: false });
        return;
      }

      const foreverMatch = trimmed.match(/^while\s+(?:True|true|1)\s*:\s*$/) || trimmed.match(/^while\s*\(\s*(?:true|1)\s*\)\s*\{?\s*$/);
      if (foreverMatch) {
        const stmt = {
          type: "control_forever",
          subStatements: [],
          line: idx + 1,
        };
        getActiveContainer().push(stmt);
        controlStack.push({ stmt, indent, isIf: false, inElse: false });
        return;
      }

      const repeatUntilMatch = trimmed.match(/^while\s+not\s+(.*?)\s*:\s*(?:#.*|\/\/.*)?$/) || trimmed.match(/^while\s*\(\s*!\s*\((.*?)\)\s*\)\s*\{?\s*(?:\/\*.*\*\/|\/\/.*)?$/);
      if (repeatUntilMatch) {
        let cond = repeatUntilMatch[1].trim();
        const isWaitUntilAnnotated = trimmed.includes("# wait_until") || trimmed.includes("// wait_until");
        if (isWaitUntilAnnotated) {
          cond = cond.replace(/#\s*wait_until.*$/, "").replace(/\/\/\s*wait_until.*$/, "").trim();
        }
        const nextNonEmptyLine = lines.slice(idx + 1).map((l) => l.trim()).find((l) => l.length > 0) || "";
        const isWaitUntilBody = isWaitUntilAnnotated || nextNonEmptyLine === "time.sleep(0.1)" || nextNonEmptyLine.startsWith("time.sleep(0.1)") || nextNonEmptyLine === "await delay(100);";

        if (isWaitUntilBody) {
          const stmt = {
            type: "control_wait_until",
            cond,
            inputs: {},
            line: idx + 1,
          };
          getActiveContainer().push(stmt);
          return;
        }

        const stmt = {
          type: "control_repeat_until",
          cond,
          subStatements: [],
          line: idx + 1,
        };
        getActiveContainer().push(stmt);
        controlStack.push({ stmt, indent, isIf: false, inElse: false });
        return;
      }

      let parsedStmt = null;

      // 1. Motion: sprite.move(10) or move(10)
      const moveMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?move\s*\(([\s\S]*)\)\s*;?$/);
      if (moveMatch) {
        const rawVal = moveMatch[2].trim() || "10";
        parsedStmt = {
          type: "motion_movesteps",
          sprite: moveMatch[1] || "",
          val: rawVal,
          inputs: { STEPS: rawVal },
          line: idx + 1,
        };
      }

      // 2. Turn Right: sprite.turn_right(15) / sprite.turnRight(15)
      if (!parsedStmt) {
        const trMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:turn_right|turnRight)\s*\(([\s\S]*)\)\s*;?$/);
        if (trMatch) {
          const rawVal = trMatch[2].trim() || "15";
          parsedStmt = {
            type: "motion_turnright",
            sprite: trMatch[1] || "",
            val: rawVal,
            inputs: { DEGREES: rawVal },
            line: idx + 1,
          };
        }
      }

      // 3. Turn Left: sprite.turn_left(15) / sprite.turnLeft(15)
      if (!parsedStmt) {
        const tlMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:turn_left|turnLeft)\s*\(([\s\S]*)\)\s*;?$/);
        if (tlMatch) {
          const rawVal = tlMatch[2].trim() || "15";
          parsedStmt = {
            type: "motion_turnleft",
            sprite: tlMatch[1] || "",
            val: rawVal,
            inputs: { DEGREES: rawVal },
            line: idx + 1,
          };
        }
      }

      // 4. Go to x, y or target: sprite.go_to(x, y) / sprite.go_to("mouse")
      if (!parsedStmt) {
        const gotoMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:go_to|goTo)\s*\(([\s\S]*)\)\s*;?$/);
        if (gotoMatch) {
          const args = splitCallArgs(gotoMatch[2]);
          if (args.length >= 2) {
            const xVal = args[0].trim() || "0";
            const yVal = args[1].trim() || "0";
            parsedStmt = {
              type: "motion_gotoxy",
              sprite: gotoMatch[1] || "",
              x: xVal,
              y: yVal,
              inputs: { X: xVal, Y: yVal },
              line: idx + 1,
            };
          } else if (args.length === 1) {
            let targetVal = args[0].replace(/["']/g, "").trim();
            parsedStmt = {
              type: "motion_goto",
              sprite: gotoMatch[1] || "",
              val: targetVal || "random position",
              inputs: {},
              line: idx + 1,
            };
          }
        }
      }

      // 4b. Glide to target: sprite.glide_to(1, "random position")
      if (!parsedStmt) {
        const glideToMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:glide_to|glideTo)\s*\(([\s\S]*)\)\s*;?$/);
        if (glideToMatch) {
          const args = splitCallArgs(glideToMatch[2]);
          const secs = args[0]?.trim() || "1";
          let targetVal = (args[1] || "random position").replace(/["']/g, "").trim();
          parsedStmt = {
            type: "motion_glideto",
            sprite: glideToMatch[1] || "",
            secs,
            val: targetVal,
            inputs: { SECS: secs },
            line: idx + 1,
          };
        }
      }

      // 4c. Glide to x, y: sprite.glide(1, 0, 0)
      if (!parsedStmt) {
        const glideMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?glide\s*\(([\s\S]*)\)\s*;?$/);
        if (glideMatch) {
          const args = splitCallArgs(glideMatch[2]);
          if (args.length >= 3) {
            parsedStmt = {
              type: "motion_glidesecstoxy",
              sprite: glideMatch[1] || "",
              secs: args[0].trim() || "1",
              x: args[1].trim() || "0",
              y: args[2].trim() || "0",
              inputs: { SECS: args[0].trim() || "1", X: args[1].trim() || "0", Y: args[2].trim() || "0" },
              line: idx + 1,
            };
          }
        }
      }

      // 4d. Point towards: sprite.point_towards("mouse")
      if (!parsedStmt) {
        const ptMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:point_towards|pointTowards)\s*\(([\s\S]*)\)\s*;?$/);
        if (ptMatch) {
          const args = splitCallArgs(ptMatch[2]);
          let targetVal = (args[0] || "mouse-pointer").replace(/["']/g, "").trim();
          parsedStmt = {
            type: "motion_pointtowards",
            sprite: ptMatch[1] || "",
            val: targetVal,
            inputs: {},
            line: idx + 1,
          };
        }
      }

      // 4e. Bounce if on edge: sprite.bounce_if_on_edge()
      if (!parsedStmt) {
        const bounceMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:bounce_if_on_edge|if_on_edge_bounce|bounceIfOnEdge)\s*\(\s*\)\s*;?$/);
        if (bounceMatch) {
          parsedStmt = {
            type: "motion_ifonedgebounce",
            sprite: bounceMatch[1] || "",
            inputs: {},
            line: idx + 1,
          };
        }
      }

      // 4f. Set rotation style: sprite.set_rotation_style("left-right")
      if (!parsedStmt) {
        const rotMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:set_rotation_style|setRotationStyle)\s*\(\s*(["'])(.*?)\2\s*\)\s*;?$/);
        if (rotMatch) {
          parsedStmt = {
            type: "motion_setrotationstyle",
            sprite: rotMatch[1] || "",
            val: rotMatch[3] || "all around",
            inputs: {},
            fields: { STYLE: [rotMatch[3] || "all around", null] },
            line: idx + 1,
          };
        }
      }

      // 5. Change X by: sprite.change_x(10) / changeX(10)
      if (!parsedStmt) {
        const cxMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:change_x|changeX)\s*\(([\s\S]*)\)\s*;?$/);
        if (cxMatch) {
          const rawVal = cxMatch[2].trim() || "10";
          parsedStmt = {
            type: "motion_changexby",
            sprite: cxMatch[1] || "",
            val: rawVal,
            inputs: { DX: rawVal },
            line: idx + 1,
          };
        }
      }

      // 6. Change Y by: sprite.change_y(10) / changeY(10)
      if (!parsedStmt) {
        const cyMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:change_y|changeY)\s*\(([\s\S]*)\)\s*;?$/);
        if (cyMatch) {
          const rawVal = cyMatch[2].trim() || "10";
          parsedStmt = {
            type: "motion_changeyby",
            sprite: cyMatch[1] || "",
            val: rawVal,
            inputs: { DY: rawVal },
            line: idx + 1,
          };
        }
      }

      // 7. Set X / Set Y
      if (!parsedStmt) {
        const sxMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:set_x|setX)\s*\(([\s\S]*)\)\s*;?$/);
        if (sxMatch) {
          const rawVal = sxMatch[2].trim() || "0";
          parsedStmt = {
            type: "motion_setx",
            sprite: sxMatch[1] || "",
            val: rawVal,
            inputs: { X: rawVal },
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const syMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:set_y|setY)\s*\(([\s\S]*)\)\s*;?$/);
        if (syMatch) {
          const rawVal = syMatch[2].trim() || "0";
          parsedStmt = {
            type: "motion_sety",
            sprite: syMatch[1] || "",
            val: rawVal,
            inputs: { Y: rawVal },
            line: idx + 1,
          };
        }
      }

      // 8. Point in direction: sprite.point_in_direction(90)
      if (!parsedStmt) {
        const dirMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:point_in_direction|pointInDirection)\s*\(([\s\S]*)\)\s*;?$/);
        if (dirMatch) {
          const rawVal = dirMatch[2].trim() || "90";
          parsedStmt = {
            type: "motion_pointindirection",
            sprite: dirMatch[1] || "",
            val: rawVal,
            inputs: { DIRECTION: rawVal },
            line: idx + 1,
          };
        }
      }

      // 9. Say / Think Message: sprite.say(...) / sprite.think(...)
      if (!parsedStmt) {
        const sayThink = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(say|think)\s*\(([\s\S]*)\)\s*;?$/);
        if (sayThink) {
          const sprite = sayThink[1] || "";
          const method = sayThink[2];
          const rawArgs = sayThink[3].trim();
          const args = splitCallArgs(rawArgs);
          const msgExpr = args[0] !== undefined ? args[0] : "";
          const secs = args[1] !== undefined ? args[1] : null;
          const dec = decomposeMessageExpr(msgExpr);

          parsedStmt = {
            type: secs !== null ? `looks_${method}forsecs` : `looks_${method}`,
            sprite,
            val: dec.val !== undefined ? dec.val : msgExpr,
            secs,
            decomposedMsg: dec,
            inputs: {
              MESSAGE: dec.val !== undefined ? dec.val : msgExpr,
              ...(secs !== null ? { SECS: secs } : {}),
            },
            line: idx + 1,
          };
        }
      }

      // 13. Show / Hide
      if (!parsedStmt) {
        const showMatch = trimmed.match(/(?:([a-zA-Z0-9_]+)\.)?show\s*\(\s*\)/);
        if (showMatch) {
          parsedStmt = {
            type: "looks_show",
            sprite: showMatch[1] || "",
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const hideMatch = trimmed.match(/(?:([a-zA-Z0-9_]+)\.)?hide\s*\(\s*\)/);
        if (hideMatch) {
          parsedStmt = {
            type: "looks_hide",
            sprite: hideMatch[1] || "",
            line: idx + 1,
          };
        }
      }

      // 14. Switch Costume: sprite.switch_costume("costume1") / switchCostume("costume1")
      if (!parsedStmt) {
        const switchCostumeMatch = trimmed.match(/(?:([a-zA-Z0-9_]+)\.)?(?:switch_costume|switchCostume)\s*\(\s*(["'])(.*?)\2\s*\)/);
        if (switchCostumeMatch) {
          parsedStmt = {
            type: "looks_switchcostumeto",
            sprite: switchCostumeMatch[1] || "",
            val: switchCostumeMatch[3],
            inputs: { COSTUME: switchCostumeMatch[3] },
            line: idx + 1,
          };
        }
      }

      // 15. Next Costume: sprite.next_costume() / nextCostume()
      if (!parsedStmt) {
        const costumeMatch = trimmed.match(/(?:([a-zA-Z0-9_]+)\.)?(?:next_costume|nextCostume)\s*\(\s*\)/);
        if (costumeMatch) {
          parsedStmt = {
            type: "looks_nextcostume",
            sprite: costumeMatch[1] || "",
            line: idx + 1,
          };
        }
      }

      // 16. Switch Backdrop: stage.switch_backdrop("backdrop1") / switchBackdrop("backdrop1")
      if (!parsedStmt) {
        const switchBackdropMatch = trimmed.match(/(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:switch_backdrop|switchBackdrop|switch_backdrop_and_wait|switchBackdropAndWait)\s*\(\s*(["'])(.*?)\2\s*\)/);
        if (switchBackdropMatch) {
          parsedStmt = {
            type: "looks_switchbackdropto",
            sprite: switchBackdropMatch[1] || null,
            val: switchBackdropMatch[3],
            inputs: { BACKDROP: switchBackdropMatch[3] },
            line: idx + 1,
          };
        }
      }

      // 17. Next Backdrop: stage.next_backdrop() / nextBackdrop()
      if (!parsedStmt) {
        const nextBackdropMatch = trimmed.match(/(?:([a-zA-Z0-9_]+)\.)?(?:next_backdrop|nextBackdrop)\s*\(\s*\)/);
        if (nextBackdropMatch) {
          parsedStmt = {
            type: "looks_nextbackdrop",
            sprite: nextBackdropMatch[1] || null,
            line: idx + 1,
          };
        }
      }

      // 18. Change Size by / Set Size to
      if (!parsedStmt) {
        const sizeMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:change_size|changeSize)\s*\(([\s\S]*)\)\s*;?$/);
        if (sizeMatch) {
          const rawVal = sizeMatch[2].trim() || "10";
          parsedStmt = {
            type: "looks_changesizeby",
            sprite: sizeMatch[1] || "",
            val: rawVal,
            inputs: { CHANGE: rawVal },
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const setSizeMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:set_size|setSize)\s*\(([\s\S]*)\)\s*;?$/);
        if (setSizeMatch) {
          const rawVal = setSizeMatch[2].trim() || "100";
          parsedStmt = {
            type: "looks_setsizeto",
            sprite: setSizeMatch[1] || "",
            val: rawVal,
            inputs: { SIZE: rawVal },
            line: idx + 1,
          };
        }
      }

      // 18b. Change effect / Set effect / Clear effects
      if (!parsedStmt) {
        const chEffMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:change_effect|changeEffect)\s*\(([\s\S]*)\)\s*;?$/);
        if (chEffMatch && chEffMatch[1] !== "sound") {
          const args = splitCallArgs(chEffMatch[2]);
          const eff = (args[0] || "color").replace(/["']/g, "").trim().toLowerCase();
          const val = args[1]?.trim() || "25";
          parsedStmt = {
            type: "looks_changeeffectby",
            sprite: chEffMatch[1] || "",
            effect: eff,
            val,
            inputs: { CHANGE: val },
            fields: { EFFECT: [eff.toUpperCase(), null] },
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const setEffMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:set_effect|setEffect)\s*\(([\s\S]*)\)\s*;?$/);
        if (setEffMatch && setEffMatch[1] !== "sound") {
          const args = splitCallArgs(setEffMatch[2]);
          const eff = (args[0] || "color").replace(/["']/g, "").trim().toLowerCase();
          const val = args[1]?.trim() || "0";
          parsedStmt = {
            type: "looks_seteffectto",
            sprite: setEffMatch[1] || "",
            effect: eff,
            val,
            inputs: { VALUE: val },
            fields: { EFFECT: [eff.toUpperCase(), null] },
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const clrEffMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:clear_graphic_effects|clearGraphicEffects|clear_effects|clearEffects)\s*\(\s*\)\s*;?$/);
        if (clrEffMatch && clrEffMatch[1] !== "sound") {
          parsedStmt = {
            type: "looks_cleargraphiceffects",
            sprite: clrEffMatch[1] || "",
            inputs: {},
            line: idx + 1,
          };
        }
      }

      // 18c. Go to layer / Change layer
      if (!parsedStmt) {
        const layerMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:go_to_layer|goToLayer)\s*\(\s*(["'])(.*?)\2\s*\)\s*;?$/);
        if (layerMatch) {
          parsedStmt = {
            type: "looks_gotofrontback",
            sprite: layerMatch[1] || "",
            val: layerMatch[3] || "front",
            inputs: {},
            fields: { FRONT_BACK: [layerMatch[3] || "front", null] },
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const chLayerMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:change_layer|changeLayer)\s*\(([\s\S]*)\)\s*;?$/);
        if (chLayerMatch) {
          const args = splitCallArgs(chLayerMatch[2]);
          const dir = (args[0] || "forward").replace(/["']/g, "").trim().toLowerCase();
          const val = args[1]?.trim() || "1";
          parsedStmt = {
            type: "looks_goforwardbackwardlayers",
            sprite: chLayerMatch[1] || "",
            dir,
            val,
            inputs: { NUM: val },
            fields: { FORWARD_BACKWARD: [dir, null] },
            line: idx + 1,
          };
        }
      }

      // 19. Wait / Sleep: time.sleep(1) / delay(1000) / sleep(1)
      if (!parsedStmt) {
        const sleepMatch = trimmed.match(/^(?:await\s+)?(?:time\.sleep|sleep|delay)\s*\(([\s\S]*)\)\s*;?$/);
        if (sleepMatch) {
          const rawArg = sleepMatch[1].trim() || "1";
          let waitVal = rawArg;
          const numVal = parseFloat(rawArg);
          if (!isNaN(numVal) && String(numVal) === rawArg) {
            let waitSecs = numVal;
            if (waitSecs >= 50 && trimmed.includes("delay")) {
              waitSecs = waitSecs / 1000;
            }
            waitVal = String(waitSecs);
          }
          parsedStmt = {
            type: "control_wait",
            val: waitVal,
            inputs: { DURATION: waitVal },
            line: idx + 1,
          };
        }
      }

      // 20. Variable assignment: score = 100 / let score = 100;
      if (!parsedStmt) {
        const varAssignMatch = trimmed.match(/^(?:let\s+|const\s+|var\s+|int\s+|float\s+|double\s+|auto\s+|String\s+)?([a-zA-Z_]\w*)\s*=\s*([-\d.]+|["'].*?["'])\s*;?$/);
        if (varAssignMatch) {
          const rawVarName = varAssignMatch[1];
          let rawVal = varAssignMatch[2];
          if ((rawVal.startsWith('"') && rawVal.endsWith('"')) || (rawVal.startsWith("'") && rawVal.endsWith("'"))) {
            rawVal = rawVal.slice(1, -1);
          }
          if (!rawVal.includes("PractidoSprite") && !rawVal.includes("Sprite(") && !rawVal.includes("Stage(")) {
            parsedStmt = {
              type: "data_setvariableto",
              varName: rawVarName,
              val: rawVal,
              inputs: { VALUE: rawVal },
              fields: { VARIABLE: [rawVarName, rawVarName] },
              line: idx + 1,
            };
          }
        }
      }

      // 20b. Variable change: score += 1 / score += 1;
      if (!parsedStmt) {
        const changeVarMatch = trimmed.match(/^([a-zA-Z_]\w*)\s*\+=\s*(.*?)\s*;?$/);
        if (changeVarMatch) {
          const rawVal = changeVarMatch[2].trim();
          parsedStmt = {
            type: "data_changevariableby",
            varName: changeVarMatch[1],
            val: rawVal,
            inputs: { VALUE: rawVal },
            fields: { VARIABLE: [changeVarMatch[1], changeVarMatch[1]] },
            line: idx + 1,
          };
        }
      }

      // 21. List: append / push -> data_addtolist
      if (!parsedStmt) {
        const addToListMatch = trimmed.match(/^(?:await\s+)?([a-zA-Z_]\w*)\.(?:append|push)\s*\(\s*(.*?)\s*\)\s*;?$/);
        if (addToListMatch && !["sprite", "stage", "time", "math", "random", "console"].includes(addToListMatch[1])) {
          let rawItem = addToListMatch[2].trim();
          if ((rawItem.startsWith('"') && rawItem.endsWith('"')) || (rawItem.startsWith("'") && rawItem.endsWith("'"))) {
            rawItem = rawItem.slice(1, -1);
          }
          parsedStmt = {
            type: "data_addtolist",
            listName: addToListMatch[1],
            item: rawItem,
            inputs: { ITEM: rawItem },
            line: idx + 1,
          };
        }
      }

      // 22. List: delete / pop / splice(idx-1, 1) -> data_deleteoflist
      if (!parsedStmt) {
        const delPyMatch = trimmed.match(/^del\s+([a-zA-Z_]\w*)\[(?:int\()?(.*?)(?:\)\s*)?\s*-\s*1\]\s*;?$/);
        const delJsMatch = trimmed.match(/^([a-zA-Z_]\w*)\.splice\s*\(\s*(.*?)\s*-\s*1\s*,\s*1\s*\)\s*;?$/);
        const popMatch = trimmed.match(/^([a-zA-Z_]\w*)\.pop\s*\(\s*(?:int\()?(.*?)(?:\)\s*)?\s*-\s*1\s*\)\s*;?$/);
        const m = delPyMatch || delJsMatch || popMatch;
        if (m) {
          parsedStmt = {
            type: "data_deleteoflist",
            listName: m[1],
            idx: m[2].trim(),
            inputs: { INDEX: m[2].trim() },
            line: idx + 1,
          };
        }
      }

      // 23. List: clear / length = 0 -> data_deletealloflist
      if (!parsedStmt) {
        const clearMatch = trimmed.match(/^([a-zA-Z_]\w*)\.(?:clear\s*\(\s*\)|length\s*=\s*0)\s*;?$/);
        if (clearMatch) {
          parsedStmt = {
            type: "data_deletealloflist",
            listName: clearMatch[1],
            inputs: {},
            line: idx + 1,
          };
        }
      }

      // 24. List: insert at index -> data_insertatlist
      if (!parsedStmt) {
        const insPyMatch = trimmed.match(/^([a-zA-Z_]\w*)\.insert\s*\(\s*(?:int\()?(.*?)(?:\)\s*)?\s*-\s*1\s*,\s*(.*?)\s*\)\s*;?$/);
        const insJsMatch = trimmed.match(/^([a-zA-Z_]\w*)\.splice\s*\(\s*(.*?)\s*-\s*1\s*,\s*0\s*,\s*(.*?)\s*\)\s*;?$/);
        const m = insPyMatch || insJsMatch;
        if (m) {
          let rawItem = m[3].trim();
          if ((rawItem.startsWith('"') && rawItem.endsWith('"')) || (rawItem.startsWith("'") && rawItem.endsWith("'"))) {
            rawItem = rawItem.slice(1, -1);
          }
          parsedStmt = {
            type: "data_insertatlist",
            listName: m[1],
            idx: m[2].trim(),
            item: rawItem,
            inputs: { INDEX: m[2].trim(), ITEM: rawItem },
            line: idx + 1,
          };
        }
      }

      // 25. List: replace item of list -> data_replaceitemoflist
      if (!parsedStmt) {
        const repMatch = trimmed.match(/^([a-zA-Z_]\w*)\[(?:int\()?(.*?)(?:\)\s*)?\s*-\s*1\]\s*=\s*(.*?)\s*;?$/);
        if (repMatch) {
          let rawItem = repMatch[3].trim();
          if ((rawItem.startsWith('"') && rawItem.endsWith('"')) || (rawItem.startsWith("'") && rawItem.endsWith("'"))) {
            rawItem = rawItem.slice(1, -1);
          }
          parsedStmt = {
            type: "data_replaceitemoflist",
            listName: repMatch[1],
            idx: repMatch[2].trim(),
            item: rawItem,
            inputs: { INDEX: repMatch[2].trim(), ITEM: rawItem },
            line: idx + 1,
          };
        }
      }

      // 26. List: show list / hide list
      if (!parsedStmt) {
        const showListCmdMatch = trimmed.match(/^(?:await\s+)?(?:show_list|showList)\s*\(\s*(["'])(.*?)\1\s*\)\s*;?$/);
        const hideListCmdMatch = trimmed.match(/^(?:await\s+)?(?:hide_list|hideList)\s*\(\s*(["'])(.*?)\1\s*\)\s*;?$/);
        const showListPyMatch = trimmed.match(/^print\s*\(\s*f?["']([a-zA-Z_]\w*):\s*\{?\1\}?["']\s*\)\s*;?$/);
        const showListJsMatch = trimmed.match(/^console\.log\s*\(\s*["']([a-zA-Z_]\w*):["']\s*,\s*\1\s*\)\s*;?$/);
        if (showListCmdMatch) {
          parsedStmt = {
            type: "data_showlist",
            listName: showListCmdMatch[2],
            inputs: {},
            line: idx + 1,
          };
        } else if (hideListCmdMatch) {
          parsedStmt = {
            type: "data_hidelist",
            listName: hideListCmdMatch[2],
            inputs: {},
            line: idx + 1,
          };
        } else if (showListPyMatch || showListJsMatch) {
          const sm = showListPyMatch || showListJsMatch;
          parsedStmt = {
            type: "data_showlist",
            listName: sm[1],
            inputs: {},
            line: idx + 1,
          };
        }
      }

      // 26a2. Show / Hide Variable
      if (!parsedStmt) {
        const showVarMatch = trimmed.match(/^(?:await\s+)?(?:show_variable|showVariable)\s*\(\s*(["'])(.*?)\1\s*\)\s*;?$/);
        if (showVarMatch) {
          parsedStmt = {
            type: "data_showvariable",
            varName: showVarMatch[2],
            inputs: {},
            fields: { VARIABLE: [showVarMatch[2], showVarMatch[2]] },
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const hideVarMatch = trimmed.match(/^(?:await\s+)?(?:hide_variable|hideVariable)\s*\(\s*(["'])(.*?)\1\s*\)\s*;?$/);
        if (hideVarMatch) {
          parsedStmt = {
            type: "data_hidevariable",
            varName: hideVarMatch[2],
            inputs: {},
            fields: { VARIABLE: [hideVarMatch[2], hideVarMatch[2]] },
            line: idx + 1,
          };
        }
      }

      // 26b. Create Clone: sprite.create_clone("myself") / create_clone("myself") / sprite.createClone("myself")
      if (!parsedStmt) {
        const cloneMatch = trimmed.match(/(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:create_clone|createClone)\s*\(\s*(?:(["'])(.*?)\2)?\s*\)\s*;?$/);
        if (cloneMatch) {
          const rawTarget = cloneMatch[3] || "myself";
          parsedStmt = {
            type: "control_create_clone_of",
            sprite: cloneMatch[1] || "",
            val: rawTarget,
            inputs: {},
            line: idx + 1,
          };
        }
      }

      // 26c. Delete This Clone: sprite.delete_this_clone() / delete_this_clone() / sprite.deleteThisClone()
      if (!parsedStmt) {
        const delCloneMatch = trimmed.match(/(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?(?:delete_this_clone|delete_clone|deleteThisClone|deleteClone)\s*\(\s*\)\s*;?$/);
        if (delCloneMatch) {
          parsedStmt = {
            type: "control_delete_this_clone",
            sprite: delCloneMatch[1] || "",
            inputs: {},
            line: idx + 1,
          };
        }
      }

      // 26d. Teachable Machine: tm.load_model("https://...") or tm.load_model("image", "https://...")
      if (!parsedStmt) {
        const tmLoadMatch = trimmed.match(/(?:await\s+)?tm\.(?:load_model|loadModel)\s*\(\s*(?:(["'])(.*?)\1\s*,\s*)?(["'])(.*?)\3\s*\)\s*;?$/);
        if (tmLoadMatch) {
          parsedStmt = {
            type: "teachableMachine_loadModelUrl",
            typeVal: tmLoadMatch[2] || "image",
            urlVal: tmLoadMatch[4] || "",
            inputs: { URL: tmLoadMatch[4] || "" },
            line: idx + 1,
          };
        }
      }

      // 26e. Teachable Machine: tm.set_video("on") / tm.setVideo("on")
      if (!parsedStmt) {
        const tmVideoMatch = trimmed.match(/(?:await\s+)?tm\.(?:set_video|setVideo)\s*\(\s*(["'])(.*?)\1(?:\s*,\s*([-\d.]+))?\s*\)\s*;?$/);
        if (tmVideoMatch) {
          parsedStmt = {
            type: "teachableMachine_setVideoState",
            val: tmVideoMatch[2] || "on",
            inputs: {},
            line: idx + 1,
          };
        }
      }

      // 26e2. Teachable Machine: tm.set_video_transparency(25) / tm.setVideoTransparency(25)
      if (!parsedStmt) {
        const tmTranspMatch = trimmed.match(/(?:await\s+)?tm\.(?:set_video_transparency|setVideoTransparency)\s*\(\s*([-\d.]+)\s*\)\s*;?$/);
        if (tmTranspMatch) {
          parsedStmt = {
            type: "teachableMachine_setVideoTransparency",
            val: tmTranspMatch[1] || "25",
            inputs: { TRANSPARENCY: tmTranspMatch[1] || "25" },
            line: idx + 1,
          };
        }
      }

      // 26f. Teachable Machine: tm.set_confidence_threshold(75) / tm.setConfidenceThreshold(75)
      if (!parsedStmt) {
        const tmThreshMatch = trimmed.match(/(?:await\s+)?tm\.(?:set_confidence_threshold|setConfidenceThreshold)\s*\(\s*([-\d.]+)\s*\)\s*;?$/);
        if (tmThreshMatch) {
          parsedStmt = {
            type: "teachableMachine_setConfidenceThreshold",
            val: tmThreshMatch[1] || "75",
            inputs: { THRESHOLD: tmThreshMatch[1] || "75" },
            line: idx + 1,
          };
        }
      }

      // 26g. Sensing: user_answer = input("...") / prompt("...")
      if (!parsedStmt) {
        const inputMatch = trimmed.match(/^(?:(?:let\s+|const\s+|var\s+)?([a-zA-Z_]\w*)\s*=\s*)?(?:await\s+)?(?:input|prompt)\s*\(\s*(?:(["'])(.*?)\2)?\s*\)\s*;?$/);
        if (inputMatch) {
          const varAssigned = inputMatch[1] || "";
          const questionText = inputMatch[3] !== undefined ? inputMatch[3] : "";
          parsedStmt = {
            type: "sensing_askandwait",
            val: questionText,
            varAssigned,
            inputs: { QUESTION: questionText },
            line: idx + 1,
          };
        }
      }

      // 26h. Events: broadcast("...") / broadcast_and_wait("...") / broadcastAndWait("...")
      if (!parsedStmt) {
        const bcastWaitMatch = trimmed.match(/^(?:await\s+)?(?:broadcast_and_wait|broadcastAndWait)\s*\(\s*(["'])(.*?)\1\s*\)\s*;?$/);
        if (bcastWaitMatch) {
          parsedStmt = {
            type: "event_broadcastandwait",
            val: bcastWaitMatch[2] || "message1",
            inputs: { BROADCAST_INPUT: bcastWaitMatch[2] || "message1" },
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const bcastMatch = trimmed.match(/^broadcast\s*\(\s*(["'])(.*?)\1\s*\)\s*;?$/);
        if (bcastMatch) {
          parsedStmt = {
            type: "event_broadcast",
            val: bcastMatch[2] || "message1",
            inputs: { BROADCAST_INPUT: bcastMatch[2] || "message1" },
            line: idx + 1,
          };
        }
      }

      // 26i. Sounds: sprite.play_sound("Meow") / play_sound_until_done("Meow") / stop_all_sounds()
      if (!parsedStmt) {
        const stopSoundsMatch = trimmed.match(/^(?:stop_all_sounds|stopAllSounds)\s*\(\s*\)\s*;?$/);
        if (stopSoundsMatch) {
          parsedStmt = {
            type: "sound_stopallsounds",
            inputs: {},
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const soundWaitMatch = trimmed.match(/(?:([a-zA-Z0-9_]+)\.)?(?:play_sound_until_done|playSoundUntilDone)\s*\(\s*(["'])(.*?)\2\s*\)\s*;?$/);
        if (soundWaitMatch) {
          parsedStmt = {
            type: "sound_playuntildone",
            sprite: soundWaitMatch[1] || "",
            val: soundWaitMatch[3] || "Meow",
            inputs: { SOUND_MENU: soundWaitMatch[3] || "Meow" },
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const soundMatch = trimmed.match(/(?:([a-zA-Z0-9_]+)\.)?(?:play_sound|playSound)\s*\(\s*(["'])(.*?)\2\s*\)\s*;?$/);
        if (soundMatch) {
          parsedStmt = {
            type: "sound_play",
            sprite: soundMatch[1] || "",
            val: soundMatch[3] || "Meow",
            inputs: { SOUND_MENU: soundMatch[3] || "Meow" },
            line: idx + 1,
          };
        }
      }

      // 26i2. Sound volume & sound effects
      if (!parsedStmt) {
        const setVolMatch = trimmed.match(/^(?:await\s+)?(?:sound\.)?(?:set_volume|setVolume)\s*\(([\s\S]*)\)\s*;?$/);
        if (setVolMatch) {
          const val = setVolMatch[1].trim() || "100";
          parsedStmt = {
            type: "sound_setvolumeto",
            val,
            inputs: { VOLUME: val },
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const chVolMatch = trimmed.match(/^(?:await\s+)?(?:sound\.)?(?:change_volume|changeVolume)\s*\(([\s\S]*)\)\s*;?$/);
        if (chVolMatch) {
          const val = chVolMatch[1].trim() || "-10";
          parsedStmt = {
            type: "sound_changevolumeby",
            val,
            inputs: { VOLUME: val },
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const soundEffMatch = trimmed.match(/^(?:await\s+)?(?:sound\.)?(?:set_effect|setEffect)\s*\(([\s\S]*)\)\s*;?$/);
        if (soundEffMatch) {
          const args = splitCallArgs(soundEffMatch[1]);
          const eff = (args[0] || "pitch").replace(/["']/g, "").trim().toLowerCase();
          const val = args[1]?.trim() || "100";
          parsedStmt = {
            type: "sound_seteffectto",
            effect: eff,
            val,
            inputs: { VALUE: val },
            fields: { EFFECT: [eff.toUpperCase(), null] },
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const soundChEffMatch = trimmed.match(/^(?:await\s+)?(?:sound\.)?(?:change_effect|changeEffect)\s*\(([\s\S]*)\)\s*;?$/);
        if (soundChEffMatch) {
          const args = splitCallArgs(soundChEffMatch[1]);
          const eff = (args[0] || "pitch").replace(/["']/g, "").trim().toLowerCase();
          const val = args[1]?.trim() || "10";
          parsedStmt = {
            type: "sound_changeeffectby",
            effect: eff,
            val,
            inputs: { VALUE: val },
            fields: { EFFECT: [eff.toUpperCase(), null] },
            line: idx + 1,
          };
        }
      }

      if (!parsedStmt) {
        const clrSoundEffMatch = trimmed.match(/^(?:await\s+)?(?:sound\.)?(?:clear_effects|clearEffects)\s*\(\s*\)\s*;?$/);
        if (clrSoundEffMatch) {
          parsedStmt = {
            type: "sound_cleareffects",
            inputs: {},
            line: idx + 1,
          };
        }
      }

      // 26j. Control: stop_all() / exit(0)
      if (!parsedStmt) {
        const stopMatch = trimmed.match(/^(?:stop_all|stopAll)\s*\(\s*\)\s*;?$/) || trimmed.match(/^exit\s*\(\s*0\s*\)(?:\s*#\s*Stop\s*(["']?)(.*?)\1)?\s*;?$/);
        if (stopMatch) {
          let stopOpt = "all";
          if (stopMatch[2]) {
            const rawOpt = stopMatch[2].toLowerCase();
            if (rawOpt.includes("this")) stopOpt = "this script";
            else if (rawOpt.includes("other")) stopOpt = "other scripts in sprite";
            else stopOpt = "all";
          }
          parsedStmt = {
            type: "control_stop",
            val: stopOpt,
            fields: { STOP_OPTION: [stopOpt, null] },
            line: idx + 1,
          };
        }
      }

      // 27. Procedure call (My Block): jump(10) / await jump(10);
      if (!parsedStmt) {
        const procCallMatch = trimmed.match(/^(?:await\s+)?(?:([a-zA-Z0-9_]+)\.)?([a-zA-Z_]\w*)\s*\(([\s\S]*)\)\s*;?$/);
        if (procCallMatch) {
          const sprite = procCallMatch[1] || "";
          const fnName = procCallMatch[2];
          const reservedWords = [
            "move", "turn_right", "turnRight", "turn_left", "turnLeft",
            "go_to", "goTo", "change_x", "changeX", "change_y", "changeY",
            "set_x", "setX", "set_y", "setY", "point_in_direction", "pointInDirection",
            "point_towards", "pointTowards", "glide", "glide_to", "glideTo",
            "bounce_if_on_edge", "if_on_edge_bounce", "bounceIfOnEdge",
            "set_rotation_style", "setRotationStyle",
            "say", "think", "show", "hide", "switch_costume", "switchCostume",
            "next_costume", "nextCostume", "switch_backdrop", "switchBackdrop",
            "next_backdrop", "nextBackdrop", "change_size", "changeSize",
            "set_size", "setSize", "change_effect", "changeEffect", "set_effect", "setEffect",
            "clear_graphic_effects", "clearGraphicEffects", "clear_effects", "clearEffects",
            "go_to_layer", "goToLayer", "change_layer", "changeLayer",
            "sleep", "delay", "print", "console",
            "when_start", "whenStart", "on_start", "onStart",
            "stop_all_sounds", "stopAllSounds", "broadcast", "broadcast_and_wait", "broadcastAndWait",
            "play_sound", "playSound", "play_sound_until_done", "playSoundUntilDone",
            "set_volume", "setVolume", "change_volume", "changeVolume",
            "show_variable", "showVariable", "hide_variable", "hideVariable",
            "show_list", "showList", "hide_list", "hideList",
            "stop_all", "stopAll", "input", "prompt",
            "when_start_as_clone", "whenStartAsClone", "on_start_as_clone", "onStartAsClone",
            "create_clone", "createClone", "delete_this_clone", "delete_clone", "deleteThisClone", "deleteClone"
          ];
          const cleanFn = fnName.toLowerCase().replace(/_+/g, "_");
          const isDefinedProc = definedProcNames.has(cleanFn) || definedProcNames.has(fnName.toLowerCase());
          if (!sprite && !reservedWords.includes(fnName) && !fnName.startsWith("when_") && !fnName.startsWith("on_") && isDefinedProc) {
            const rawArgs = procCallMatch[3].trim();
            const argsList = rawArgs ? splitCallArgs(rawArgs).map((a) => {
              let v = a.trim();
              if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
                v = v.slice(1, -1);
              }
              return v;
            }) : [];
            parsedStmt = {
              type: "procedures_call",
              sprite,
              procName: cleanFn,
              args: argsList,
              line: idx + 1,
            };
          }
        }
      }

      // 28. Standalone Block / Loose Reporter Handler
      if (!parsedStmt) {
        // A. Print / console.log wrapper:
        // print("label:", expr) or print(f"label: {expr}") or print(expr)
        const printMatch = trimmed.match(/^(?:print|console\.log)\s*\(([\s\S]*)\)\s*;?$/);
        if (printMatch) {
          const rawInner = printMatch[1].trim();
          let exprToParse = null;

          // Check for label comma format: print("label:", expr)
          const commaMatch = rawInner.match(/^(?:["'].*?["']\s*,\s*)([\s\S]+)$/);
          if (commaMatch) {
            exprToParse = commaMatch[1].trim();
          } else {
            // Check for f-string format: f"label: {expr}"
            const fStrOpen = rawInner.indexOf("{");
            const fStrClose = rawInner.lastIndexOf("}");
            if (fStrOpen !== -1 && fStrClose > fStrOpen) {
              exprToParse = rawInner.substring(fStrOpen + 1, fStrClose).trim();
            } else {
              exprToParse = rawInner;
            }
          }

          if (exprToParse) {
            const decomposed = decomposeExpression(exprToParse);
            if (decomposed && decomposed.type && decomposed.type !== "primitive") {
              parsedStmt = {
                type: decomposed.type,
                isStandaloneReporter: true,
                decomposed,
                line: idx + 1,
              };
            }
          }
        }
      }

      // B. Bare loose expression: sprite1.is_touching("edge"), sprite1.x, etc.
      if (!parsedStmt && !inFunction) {
        const isBareReporter = /^(?:([a-zA-Z0-9_]+)\.)?(?:is_touching|isTouching|touching|is_touching_color|isTouchingColor|distance_to|distanceTo|is_pressed|isPressed|x|y|direction|size)\b/.test(trimmed) ||
          /^(?:user_answer|answer|mouse_x|mouse\.x|mouse_y|mouse\.y|mouse_down|mouse\.isDown|timer|days_since_2000)\b/.test(trimmed);
        if (isBareReporter) {
          const decomposed = decomposeExpression(trimmed);
          if (decomposed && decomposed.type && decomposed.type !== "primitive") {
            parsedStmt = {
              type: decomposed.type,
              isStandaloneReporter: true,
              decomposed,
              line: idx + 1,
            };
          }
        }
      }

      if (parsedStmt) {
        getActiveContainer().push(parsedStmt);
      } else {
        // Non-comment line that couldn't be parsed into a known statement
        unparsedLines.push({ line: idx + 1, text: trimmed });
        getActiveContainer().push({
          type: "unparsed_fallback",
          originalLine: trimmed,
          line: idx + 1,
        });
      }
    });

    finalizeCurrentStack();

    // Walk through project targets and apply cluster-aware synchronization
    updatedProject.targets.forEach((target) => {
      if (target.isOriginal === false) return;
      const spriteName = target.name || "Sprite1";
      const cleanTargetName = spriteName.toLowerCase().replace(/[^a-z0-9_]/g, "_");
      const isStage = !!target.isStage || target.name === "Stage";

      // If targetName is provided, only sync the requested target
      if (targetName && targetName !== "_all_") {
        const isRequestedStage = targetName.toLowerCase() === "stage" || targetName === "_stage_";
        if (isRequestedStage && !isStage) return;
        if (!isRequestedStage && (isStage || (spriteName !== targetName && cleanTargetName !== targetName.toLowerCase()))) {
          return;
        }
      }

      if (!target.blocks) target.blocks = {};
      const blocks = target.blocks;
      const prevBlocksCount = Object.keys(blocks || {}).length;

      // Extract existing top-level block chains in canonical (y, x) visual order
      const existingTopKeys = sortTopLevelBlockKeys(blocks);
      const existingChains = existingTopKeys.map((topKey) => {
        const chain = [];
        let curr = topKey;
        while (curr && blocks[curr]) {
          chain.push(curr);
          curr = blocks[curr].next;
        }
        const topBlock = blocks[topKey];
        const isEventHat = !!(topBlock && topBlock.opcode && topBlock.opcode.startsWith("event_"));
        const isProcDef = !!(topBlock && topBlock.opcode === "procedures_definition");
        const isCloneHat = !!(topBlock && topBlock.opcode === "control_start_as_clone");
        const isTMHat = !!(topBlock && topBlock.opcode === "teachableMachine_whenClassDetected");
        const isHat = isEventHat || isProcDef || isCloneHat || isTMHat;

        let procName = null;
        let protoId = null;
        let argNames = [];
        let argIds = [];
        if (isProcDef && topBlock.inputs && topBlock.inputs.custom_block) {
          const cb = topBlock.inputs.custom_block;
          protoId = Array.isArray(cb) ? cb[1] : (cb.block || cb.shadow);
          const protoBlock = protoId && blocks[protoId];
          if (protoBlock && protoBlock.mutation) {
            const proccode = protoBlock.mutation.proccode || "";
            procName = proccode.replace(/%[snb]/g, "").trim().replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
            try {
              argNames = JSON.parse(protoBlock.mutation.argumentnames || "[]");
              argIds = JSON.parse(protoBlock.mutation.argumentids || "[]");
            } catch (e) {}
          }
        }

        return {
          topKey,
          chain,
          isHat,
          isProcDef,
          procName,
          protoId,
          argNames,
          argIds,
          hatOpcode: isHat ? topBlock.opcode : null,
          hatKey: isHat && topBlock.fields?.KEY_OPTION ? topBlock.fields.KEY_OPTION[0] : null,
          hatMsg: isHat && topBlock.fields?.BROADCAST_OPTION ? topBlock.fields.BROADCAST_OPTION[0] : null,
          hatBackdrop: isHat && topBlock.fields?.BACKDROP ? topBlock.fields.BACKDROP[0] : null,
          bodyChainKeys: isHat ? chain.slice(1) : chain,
          matched: false,
        };
      });

      // Filter parsed stacks for this target
      let targetStacks;
      if (targetName && targetName !== "_all_") {
        // Multi-file sprite isolation: all statements written in this file belong to this target
        targetStacks = parsedStacks;
      } else {
        targetStacks = parsedStacks
          .map((stack) => {
            const filteredStmts = stack.statements.filter((stmt) => {
              if (stmt.isStageOnly) {
                return isStage;
              }
              if (stmt.sprite) {
                return stmt.sprite.toLowerCase() === cleanTargetName || (isStage && stmt.sprite.toLowerCase() === "stage");
              }
              if (isStage) return false;
              return true;
            });
            const hatMatchesTarget = !stack.hat || (stack.hat.opcode === "event_whenbackdropswitchesto" ? isStage : !isStage);
            return {
              hat: hatMatchesTarget ? stack.hat : null,
              statements: filteredStmts,
            };
          })
          .filter((stk) => stk.statements.length > 0 || stk.hat);
      }

      // If user cleared or deleted all code for this target
      if (targetStacks.length === 0) {
        const hasCodeLines = lines.some((l) => {
          const t = l.trim();
          return t && !t.startsWith("#") && !t.startsWith("//") && !t.startsWith("import ") && !t.startsWith("from ");
        });
        if (hasCodeLines) {
          mutationLogs.push({
            type: "warning",
            text: `⚠️ Could not parse statements for [${spriteName}]. Workspace blocks preserved to prevent data loss.`,
          });
          return;
        }

        const hadBlocks = Object.keys(blocks).length > 0;
        if (hadBlocks) {
          target.blocks = {};
          if (target.comments) target.comments = {};
          mutationsCount++;
          mutationLogs.push({
            type: "success",
            text: `🧹 Cleared all blocks for [${spriteName}]`,
          });
        }
        return;
      }

      // Track the ordered top-level block keys produced by targetStacks to enforce deterministic vertical layout
      const orderedStackRecords = [];

      // Process each statement stack
      targetStacks.forEach((stackObj) => {
        const { hat, statements: stackStmts } = stackObj;

        // Try to match an existing chain
        let existingChainObj = null;
        if (hat) {
          existingChainObj = existingChains.find((c) => {
            if (c.matched || !c.isHat) return false;
            if (c.hatOpcode !== hat.opcode) return false;
            if (hat.opcode === "procedures_definition") {
              const normHat = (hat.procName || "").replace(/_+/g, "_").toLowerCase();
              const normC = (c.procName || "").replace(/_+/g, "_").toLowerCase();
              if (normHat && normC && normHat !== normC) return false;
              return true;
            }
            if (hat.key && c.hatKey && c.hatKey.toLowerCase() !== hat.key.toLowerCase()) return false;
            if (hat.msg && c.hatMsg && c.hatMsg.toLowerCase() !== hat.msg.toLowerCase()) return false;
            if (hat.backdrop && c.hatBackdrop && c.hatBackdrop.toLowerCase() !== hat.backdrop.toLowerCase()) return false;
            return true;
          });
        } else {
          existingChainObj = existingChains.find((c) => !c.matched && !c.isHat);
        }

        if (existingChainObj) {
          existingChainObj.matched = true;
          let stackTopKey = existingChainObj.topKey;
          const logCtx = { spriteName, mutationsCount, mutationLogs, hasUnparsed: unparsedLines.length > 0 };

          let procContext = null;
          if (existingChainObj.isProcDef) {
            const paramMap = {};
            const scratchArgNames = existingChainObj.argNames || [];
            const scratchArgIds = existingChainObj.argIds || [];
            const pyArgNames = hat?.argNames || [];
            let isBoolList = [];
            if (existingChainObj.protoId && blocks[existingChainObj.protoId]?.mutation?.proccode) {
              const pc = blocks[existingChainObj.protoId].mutation.proccode;
              const typeMatches = pc.match(/%[snb]/g) || [];
              isBoolList = typeMatches.map((t) => t === "%b");
            }

            scratchArgNames.forEach((origName, idx) => {
              const isBool = !!isBoolList[idx];
              const info = { originalName: origName, id: scratchArgIds[idx] || `input${idx}`, isBoolean: isBool };
              paramMap[origName.toLowerCase()] = info;
              paramMap[origName.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase()] = info;
              paramMap[origName.replace(/[^a-zA-Z0-9_]/g, "_").replace(/_+/g, "_").toLowerCase()] = info;
            });

            pyArgNames.forEach((pyName, idx) => {
              const origName = scratchArgNames[idx] || pyName;
              const isBool = !!isBoolList[idx];
              const info = { originalName: origName, id: scratchArgIds[idx] || `input${idx}`, isBoolean: isBool };
              paramMap[pyName.toLowerCase()] = info;
              paramMap[pyName.replace(/_+/g, "_").toLowerCase()] = info;
            });

            procContext = {
              isProcDef: true,
              procName: existingChainObj.procName,
              argNames: scratchArgNames,
              argIds: scratchArgIds,
              paramMap,
            };
          }

          if (existingChainObj.isHat) {
            const hatBlock = blocks[existingChainObj.topKey];
            const firstKey = reconcileBlockChain(blocks, existingChainObj.bodyChainKeys, stackStmts, updatedProject, target, hatBlock.id, null, logCtx, procContext);
            hatBlock.next = firstKey || null;
            if (firstKey && blocks[firstKey]) {
              blocks[firstKey].parent = hatBlock.id;
            }
          } else {
            const firstKey = reconcileBlockChain(blocks, existingChainObj.bodyChainKeys, stackStmts, updatedProject, target, null, null, logCtx, procContext);
            if (firstKey && blocks[firstKey]) {
              blocks[firstKey].topLevel = true;
              blocks[firstKey].parent = null;
              stackTopKey = firstKey;
            } else {
              stackTopKey = null;
            }
          }
          mutationsCount = logCtx.mutationsCount;

          if (stackTopKey && blocks[stackTopKey]) {
            orderedStackRecords.push({ topKey: stackTopKey, wasExisting: true });
          }
        } else {
          // Brand new stack
          if (unparsedLines.length > 0) {
            mutationLogs.push({
              type: "warning",
              text: `⚠️ Unparsed lines detected (${unparsedLines.map((u) => "L" + u.line).join(", ")}). Skipped creating new stack to prevent accidental block duplication.`,
            });
            return;
          }
          let stackTopKey = null;
          const logCtx = { spriteName, mutationsCount, mutationLogs, hasUnparsed: false };

          if (hat) {
            if (hat.opcode === "procedures_definition") {
              const defBlock = buildNewBlock("procedures_definition", {}, {});
              defBlock.topLevel = true;
              defBlock.parent = null;
              defBlock.x = 80;
              defBlock.y = 80;

              const protoBlockId = generateBlockId();
              const argIds = (hat.argNames || []).map(() => "arg_" + Math.random().toString(36).substring(2, 8));
              const argDefaults = (hat.argNames || []).map(() => "");
              const proccode = hat.procName + (hat.argNames && hat.argNames.length > 0 ? " " + hat.argNames.map(() => "%s").join(" ") : "");

              blocks[protoBlockId] = {
                id: protoBlockId,
                opcode: "procedures_prototype",
                next: null,
                parent: defBlock.id,
                inputs: {},
                fields: {},
                shadow: true,
                topLevel: false,
                mutation: {
                  tagName: "mutation",
                  children: [],
                  proccode: proccode,
                  argumentids: JSON.stringify(argIds),
                  argumentnames: JSON.stringify(hat.argNames || []),
                  argumentdefaults: JSON.stringify(argDefaults),
                  warp: "false",
                },
              };

              defBlock.inputs = { custom_block: [1, protoBlockId] };
              blocks[defBlock.id] = defBlock;

              const paramMap = {};
              (hat.argNames || []).forEach((name, idx) => {
                const info = { originalName: name, id: argIds[idx], isBoolean: false };
                paramMap[name.toLowerCase()] = info;
                paramMap[name.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase()] = info;
                paramMap[name.replace(/[^a-zA-Z0-9_]/g, "_").replace(/_+/g, "_").toLowerCase()] = info;
              });
              const procContext = {
                isProcDef: true,
                procName: hat.procName,
                argNames: hat.argNames || [],
                argIds,
                paramMap,
              };

              const firstKey = reconcileBlockChain(blocks, [], stackStmts, updatedProject, target, defBlock.id, null, logCtx, procContext);
              defBlock.next = firstKey || null;
              if (firstKey && blocks[firstKey]) {
                blocks[firstKey].parent = defBlock.id;
              }
              stackTopKey = defBlock.id;
              mutationsCount++;
              mutationLogs.push({
                type: "success",
                text: `✨ Created custom block definition [${spriteName}] ${hat.procName}`,
              });
            } else {
              const hatBlock = buildNewBlock(hat.opcode || hat.type, {}, hat.fields || {});
              if ((hat.opcode || hat.type) === "teachableMachine_whenClassDetected") {
                setShadowMenuInput(blocks, hatBlock, "CLASS", "teachableMachine_menu_classesMenu", "classesMenu", hat.tmClass || "Thumbs Up");
              }
              hatBlock.topLevel = true;
              hatBlock.parent = null;
              hatBlock.x = 80;
              hatBlock.y = 80;
              blocks[hatBlock.id] = hatBlock;

              const firstKey = reconcileBlockChain(blocks, [], stackStmts, updatedProject, target, hatBlock.id, null, logCtx);
              hatBlock.next = firstKey || null;
              if (firstKey && blocks[firstKey]) {
                blocks[firstKey].parent = hatBlock.id;
              }
              stackTopKey = hatBlock.id;
              mutationsCount++;
              mutationLogs.push({
                type: "success",
                text: `✨ Created event stack [${spriteName}] ${(hatBlock.opcode || "").replace(/_/g, " ")}`,
              });
            }
          } else {
            const firstKey = reconcileBlockChain(blocks, [], stackStmts, updatedProject, target, null, null, logCtx);
            if (firstKey && blocks[firstKey]) {
              blocks[firstKey].topLevel = true;
              blocks[firstKey].parent = null;
              blocks[firstKey].x = 80;
              blocks[firstKey].y = 80;
              stackTopKey = firstKey;
            }
          }
          mutationsCount = logCtx.mutationsCount;

          if (stackTopKey && blocks[stackTopKey]) {
            orderedStackRecords.push({ topKey: stackTopKey, wasExisting: false });
          }
        }
      });

      // PRUNE UNMATCHED CHAINS (e.g. user deleted an entire function or stack from code)
      // Safety Shield: Only prune unmatched chains if there are NO unparsed code lines in the sprite
      if (unparsedLines.length === 0) {
        existingChains.forEach((chainObj) => {
          if (!chainObj.matched) {
            chainObj.chain.forEach((k) => {
              deleteBlockAndShadows(blocks, k);
            });
            mutationsCount++;
            mutationLogs.push({
              type: "info",
              text: `🗑️ Removed deleted stack [${spriteName}] (was ${chainObj.hatOpcode ? chainObj.hatOpcode.replace(/_/g, " ") : "loose stack"})`,
            });
          }
        });
      } else {
        mutationLogs.push({
          type: "warning",
          text: `⚠️ Unparsed lines detected (${unparsedLines.map((u) => "L" + u.line).join(", ")}). Preserved unmatched block stacks to prevent accidental data loss.`,
        });
      }

      // DETERMINISTIC VERTICAL STACK PLACEMENT:
      // Ensure all top-level stacks on the canvas are ordered strictly top-to-bottom (y_0 < y_1 < ... < y_{k-1})
      // matching the exact sequence in the user's code so blocks never appear above preceding blocks
      // and round-trip transpilation never shifts code line sequence.
      if (orderedStackRecords.length > 0) {
        const firstExistingRec = orderedStackRecords.find((r) => r.wasExisting && blocks[r.topKey]);
        const baseX = firstExistingRec
          ? Math.max(60, Number(blocks[firstExistingRec.topKey].x) || 80)
          : 80;

        let cursorY = 80;
        if (firstExistingRec) {
          const firstExistingIdx = orderedStackRecords.indexOf(firstExistingRec);
          const anchorY = Math.max(60, Number(blocks[firstExistingRec.topKey].y) || 80);
          if (firstExistingIdx === 0) {
            cursorY = anchorY;
          } else {
            let heightBeforeAnchor = 0;
            for (let i = 0; i < firstExistingIdx; i++) {
              heightBeforeAnchor += countBlocksInStack(orderedStackRecords[i].topKey, blocks) * 54 + 70;
            }
            cursorY = Math.max(60, anchorY - heightBeforeAnchor);
          }
        }

        orderedStackRecords.forEach((rec, idx) => {
          const topBlock = blocks[rec.topKey];
          if (!topBlock) return;

          if (!rec.wasExisting) {
            topBlock.x = baseX;
            topBlock.y = cursorY;
          } else {
            if (topBlock.x === undefined || topBlock.x === null) {
              topBlock.x = baseX;
            }
            const currentY = Number(topBlock.y) || 80;
            if (idx === 0) {
              topBlock.y = Math.max(60, currentY);
            } else if (currentY < cursorY) {
              topBlock.y = cursorY;
            }
          }

          const stackHeight = countBlocksInStack(rec.topKey, blocks) * 54 + 70;
          cursorY = (Number(topBlock.y) || 80) + stackHeight;
        });
      }

      // Check for block deletion anomaly: unexpected drop of >30% blocks when there are NO syntax errors
      const newBlocksCount = Object.keys(target.blocks || {}).length;
      if (prevBlocksCount > 2 && newBlocksCount < prevBlocksCount * 0.7 && unparsedLines.length === 0) {
        anomalies.push({
          type: "BLOCK_DELETION_ANOMALY",
          severity: "warning",
          message: `Unexpected block count reduction from ${prevBlocksCount} to ${newBlocksCount} for target "${spriteName}"`,
          targetName: spriteName,
          prevCount: prevBlocksCount,
          newCount: newBlocksCount,
          blocksLost: prevBlocksCount - newBlocksCount,
          code: codeText,
          lang,
        });
      }
    });

    return {
      success: true,
      updatedProject,
      mutationsCount,
      mutationLogs,
      hasWarnings: unparsedLines.length > 0,
      unparsedLines,
      anomalies,
    };
  } catch (err) {
    return {
      success: false,
      error: err.message,
    };
  }
};
